import React, { useEffect, useRef, useState, useCallback } from 'react';
import { fabric } from 'fabric-with-gestures';
import TextTools from './modules/text/tool';
import installTextArc from './modules/text/arc';
import ImageTools from './modules/image/tool';
import enableHighQualityDownscaling from './modules/image/renderQuality';
import restoreOnContextLoss from './modules/canvas/contextRestore';
import { clickControl } from './modules/canvas/controlClick';
import colorImageUrl from './modules/image/colorUrl';
import fixControlTransforms from './modules/geometry/controlTransform';
import { controlSpacing, minScaleForSpacing, flooredScale } from './modules/geometry/controlSpacing';
import ContrastTools, { contrastRatio } from './modules/contrast/tool';
import GridTools from './modules/grid/tool';
import DownloadTools from './modules/download/tool';
import FPDLoaderTools from './modules/fpd_loader/tool';
import Logger from './modules/logger/logger';
import { buildRegion, polygonBounds, groupBoxes, groupAtPoint, EMPTY_REGION } from './modules/geometry/region';
import {
	objectPolygon,
	objectCenter,
	isObjectCovered,
	capturePose,
	applyPose,
	capObjectScale,
	enforceObjectInRegion,
	clampScale
} from './modules/geometry/fabricRegion';
import { PLACEMENT_OFFSET } from './modules/geometry/placement';
import { centerSnap, snapBoundsFor, SNAP_THRESHOLD } from './modules/geometry/centerSnap';
import { findColor } from './modules/colors/colors';
import fetchJson from './modules/network/fetchJson';
import { load } from 'opentype.js';

const MAX_WIDTH = 900;
const MAX_HEIGHT = 950;
// How far a copy lands from the element it came from - the same step a newly
// added element is nudged by when it would land on one that is already there.
const CLONE_OFFSET = PLACEMENT_OFFSET;
// Perceived brightness (0-255) under which a shirt counts as dark.
const DARK_SHIRT_BRIGHTNESS = 125;
const WHITE_RGB = [255, 255, 255];
// Shown on the PDF design proof under "Bedrukking" until the shop page sends
// the method the customer actually chose.
const DEFAULT_PRINT_METHOD = 'Digitale transferdruk';
// Charcoal. Shared by both spellings below so the two cannot drift apart.
const DARK_GREY_RGB = [84, 78, 85];
// Shirt colours the browser's own colour table gets wrong for this garment. Two
// kinds end up here: names it does not know at all, and - less obviously - names
// it DOES know but as a different colour from the shirt the shop sells under
// them. Ash is the first kind, a flecked white the browser rejects outright, so
// without an entry it fell through to a default meant for a dark garment and put
// a white selection border on a white shirt.
//
// The console warns by name for the first kind only. The second kind resolves
// cleanly and so says nothing at all - the only cue is a box or border that
// looks wrong on the shirt, which is what to add an entry for.
const SHIRT_COLOR_OVERRIDES = {
	ash: WHITE_RGB,
	// #800020. A dark wine red, and the default it fell through to was a black
	// bounding box on an all but black shirt. At 42 it is well under the dark
	// threshold, so the box goes white.
	burgundy: [128, 0, 32],
	// #544e55, and the second kind: CSS darkgrey is #A9A9A9, a mid grey that
	// comes out at brightness 169 and asked for a black bounding box on a shirt
	// that is nearly black. The real garment sits at 81, well under the
	// threshold, so the box goes white. Both spellings - the name comes from the
	// shop, and CSS itself defines darkgrey and darkgray alike.
	darkgrey: DARK_GREY_RGB,
	darkgray: DARK_GREY_RGB
};
// Contrast against white under which a white selection border stops reading as a
// line on the garment. At this setting the shirt palette lands as: white and ash
// 1.00, beige 1.11, khaki 1.28, lime 1.37 and light grey 1.497 go black; light blue
// 1.53 and everything lighter in contrast stays white. Light grey clears it by
// three thousandths, so nudge this if that shirt ever looks wrong - it is the one
// colour sitting on the edge.
const WHITE_BORDER_MIN_CONTRAST = 1.5;
// How close to a multiple of 90 degrees a rotation has to get before it snaps to it.
const RIGHT_ANGLE_SNAP_DEGREES = 5;
// Room left on each side of the shirt when mobile zooms in on it, in screen
// pixels. The sleeves are the widest point, and with nothing to spare a shirt
// whose picture is not perfectly centred had a sleeve cut off by the screen edge.
const MOBILE_SHIRT_PADDING_X = 16;
const MOBILE_SHIRT_PADDING_Y = 20;
// While the customer types on mobile, the text being edited is zoomed in on and
// kept in the part of the screen the keyboard leaves free. It fills at most this
// share of the screen width, and of the height of that free band - the height cap
// is what stops a single letter from being blown up to fill the screen.
const TEXT_FOCUS_MAX_WIDTH = 0.85;
const TEXT_FOCUS_MAX_HEIGHT = 0.25;
// Labels shown while the pointer rests on a control. Only duplicate and delete:
// the scale and rotate handles read well enough from their icons. Keyed by element
// type as well, so a photo is not told it is text.
const CONTROL_HINTS = {
	cloneControl: { text: 'Tekst dupliceren', image: 'Afbeelding dupliceren' },
	deleteControl: { text: 'Tekst verwijderen', image: 'Afbeelding verwijderen' }
};
// Distance from the centre of the control to the near edge of the label. Clears
// the largest corner icon (48px on mobile, so 24 from its centre) with room left.
const CONTROL_HINT_GAP = 30;

// Toolbar floating above the selected image. Its gap is measured from the top edge
// of the element and has to clear the duplicate and delete controls, which are
// centred on the top corners and so reach half a corner icon (24px at most) above
// that edge.
const ELEMENT_TOOLBAR_GAP = 36;
const ELEMENT_TOOLBAR_WIDTH = 96;
const ELEMENT_TOOLBAR_HEIGHT = 52;
// Labels shown while the pointer rests on one of its buttons, in the same style as
// the control labels above.
const ELEMENT_TOOLBAR_HINTS = {
	edit: 'Afbeelding aanpassen',
	removeBackground: 'Achtergrond verwijderen'
};
const ELEMENT_TOOLBAR_BUTTON = {
	width: 40,
	height: 40,
	padding: 0,
	border: 'none',
	borderRadius: '50%',
	background: 'transparent',
	cursor: 'pointer',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center'
};

// Magnetic around every quarter turn: within a few degrees of 0, 90, 180 or 270 the
// element clicks to it, so putting one level or exactly sideways does not take a
// steady hand. The angle wraps at 360, so a hair short of a full turn lands on 0.
function snapToRightAngle(angle) {
	const normalised = ((angle % 360) + 360) % 360;
	const nearest = Math.round(normalised / 90) * 90;
	if (Math.abs(normalised - nearest) <= RIGHT_ANGLE_SNAP_DEGREES) return nearest % 360;
	return angle;
}

// The table above and the browser lookup below have to see a name in the same
// shape, so they agree on what "Ash" and "ash" are. Hyphens and runs of whitespace
// go with the spaces: "Light-Grey" and "Light  Grey" are both the colour the
// browser knows as "lightgrey".
function normaliseColorName(colorName) {
	return String(colorName).trim().toLowerCase().replace(/[\s-]+/g, '');
}

// The names to try for a garment colour, longest first.
//
// Garment names carry modifiers the browser has never heard of - "Electric Blue",
// "Heather Grey", "Deep Navy" - and asking for the whole name is the only thing
// that used to happen, so every one of them resolved to nothing and the brightness
// test never ran. The whole name still goes first, because plenty of two-word
// garment names ARE colours the browser knows ("Light Blue", "Sky Blue", "Dark
// Green"); only when that fails are the modifiers dropped one at a time, until
// what is left is the base colour. It is the last word that decides which family a
// garment belongs to, and so whether it is dark or light; the modifiers shift it
// within that family.
function colorNameCandidates(colorName) {
	const words = String(colorName).trim().toLowerCase().split(/[\s-]+/).filter(Boolean);
	return words.map((_, i) => words.slice(i).join(''));
}

// Resolve a shirt colour name to rgb by handing the name to a hidden element and
// letting the browser's own colour table answer. Null when nothing here recognises
// the name, which is why the callers below each pick their own safe default rather
// than assuming one.
function shirtRgb(colorName) {
	if (!colorName) return null;

	// Trade names first - the browser would reject them, and the base-word fallback
	// is the wrong answer for a garment that is white in all but name.
	const override = SHIRT_COLOR_OVERRIDES[normaliseColorName(colorName)];
	if (override) return [...override];

	const el = document.getElementById('convertColorDiv');
	if (!el) return null;

	for (const candidate of colorNameCandidates(colorName)) {
		// Cleared first: an unknown name leaves the property at whatever it held
		// before, so without this the check below would compare against the last
		// candidate that WAS accepted rather than against nothing.
		el.style.color = '';
		el.style.color = candidate;
		if (el.style.color !== candidate) continue;
		const parts = window.getComputedStyle(el).color.match(/\d+/g);
		if (!parts) continue;
		return parts.slice(0, 3).map(Number);
	}

	warnUnknownShirtColor(colorName);
	return null;
}

// A colour that resolves to nothing is not an error - the callers have defaults -
// but it does mean a garment is being judged by a guess, so say which one. Once
// per name: loadSide asks twice on every side and colour switch.
const warnedShirtColors = new Set();
function warnUnknownShirtColor(colorName) {
	const key = normaliseColorName(colorName);
	if (warnedShirtColors.has(key)) return;
	warnedShirtColors.add(key);
	console.warn(
		`Unknown shirt colour "${colorName}": bounding box and selection border are falling back to a default. ` +
		'Add it to SHIRT_COLOR_OVERRIDES if they look wrong on this garment.'
	);
}

// Bounding boxes and snap lines: simply the opposite of the shirt. These are large
// shapes meant to read as the counterpart of the garment, so a straight midpoint is
// the right call.
function contrastColorForShirt(colorName) {
	const rgb = shirtRgb(colorName);
	if (!rgb) return 'black';
	const brightness = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;
	return brightness < DARK_SHIRT_BRIGHTNESS ? 'white' : 'black';
}

// The selection border is a thin line, and white is what it should be nearly
// always - so it stays white until white genuinely stops being visible. That is a
// different question from the one above, and the midpoint rule answers it wrongly:
// on mid grey it already flips to black while white still stands out against it at
// a contrast of 3.95. Only near-white, yellow, pastel and other very light shirts
// drop below the threshold and get a black border.
function selectionBorderColorForShirt(colorName) {
	const rgb = shirtRgb(colorName);
	if (!rgb) return 'white';
	return contrastRatio(WHITE_RGB, rgb) < WHITE_BORDER_MIN_CONTRAST ? 'black' : 'white';
}
// Who gets to decide the shirt colour when two answers arrive out of order.
const COLOR_SOURCE_RANK = { default: 0, stored: 1, parent: 2 };
const SITE_URL = 'https://staging.shirt-discounter.nl/';

export default function Customizer() {
	const canvasRef = useRef(null);
	const textToolsInstanceRef = useRef(null);
	const imageToolsInstanceRef = useRef(null);
	const downloadToolsInstanceRef = useRef(null);
	const gridToolsInstanceRef = useRef(null);
	const fpdLoaderToolsInstanceRef = useRef(null);
	const contrastToolsInstanceRef = useRef(null);
	// Border colour for a selected element, kept in step with the shirt by loadSide.
	const selectionBorderColorRef = useRef('white');
  	const [currentSide, setCurrentSide] = useState('front');
	const currentSideRef = useRef(currentSide);
  	const [designs, setDesigns] = useState({});
	const designsRef = useRef(designs);
	const [boundingBoxes, setBoundingBoxes] = useState({
		front: [],
		back: [],
		left: [],
		right: []
		//{ id: 1, top: 250, left: 320, width: 320, height: 500, default: true },
		//{ top: 100, left: 500, width: 200, height: 200, default: false }
	});
  	const boundingBoxesRef = useRef(boundingBoxes);
	const [activeBoundingBox, setActiveBoundingBox] = useState(null);
	const activeBoundingBoxRef = useRef(activeBoundingBox);
	// Placement is constrained by the UNION of the boxes on a side, so an element
	// may span several boxes where they touch, but never bridge a gap between
	// them. The union is decomposed into disjoint pieces once per side here; the
	// per-frame coverage test then just sums clipped areas.
	const regionsRef = useRef({ front: EMPTY_REGION, back: EMPTY_REGION, left: EMPTY_REGION, right: EMPTY_REGION });
	// Boxes that overlap or touch are one region to the user: it gets a single
	// outline and a single insert button, on its biggest box.
	const boxGroupsRef = useRef({ front: [], back: [], left: [], right: [] });
	useEffect(() => { designsRef.current = designs; }, [designs]);
	useEffect(() => { boundingBoxesRef.current = boundingBoxes; }, [boundingBoxes]);
	useEffect(() => {
		regionsRef.current = {
			front: buildRegion(boundingBoxes.front),
			back: buildRegion(boundingBoxes.back),
			left: buildRegion(boundingBoxes.left),
			right: buildRegion(boundingBoxes.right)
		};
		boxGroupsRef.current = {
			front: groupBoxes(boundingBoxes.front),
			back: groupBoxes(boundingBoxes.back),
			left: groupBoxes(boundingBoxes.left),
			right: groupBoxes(boundingBoxes.right)
		};
		// The boxes arrive from their own fetch, which can land after a stored
		// design has already been drawn - and the print areas can have been
		// resized in the admin since that design was saved. Either way the
		// elements on the canvas have never been measured against these boxes,
		// so measure them now.
		enforceAllInRegion(canvasRef.current);
	}, [boundingBoxes]);
	useEffect(() => { activeBoundingBoxRef.current = activeBoundingBox; }, [activeBoundingBox]);
  	const userId = 'userA';
	const params = new URLSearchParams(window.location.search);
	const [productId, setProductId] = useState(params.get('productId') || 'e150');
	const [sessionId, setSessionId] = useState(params.get('a') || null);
  	const [colors, setColors] = useState([]);
	const [sides, setSides] = useState(['front', 'back', 'left', 'right'])
  	const [selectedShirtColor, setSelectedShirtColor] = useState('orange');
  	const selectedShirtColorRef = useRef(selectedShirtColor);
	// Where the current colour came from. Four independent async paths pick a
	// colour - the colour list, the stored design, the parent frame and the FPD
	// order - and they finish in whatever order the network hands them back, so
	// last-writer-wins used to let a late answer undo an explicit choice. Rank
	// them instead: an explicit choice outranks a design that was stored before
	// it, which outranks the first colour of the list.
	const colorSourceRef = useRef('default');
	// A selectColor that arrives before /api/colors has answered cannot be
	// resolved to a colour yet. Park it here rather than dropping it - dropping
	// it is what left the canvas on the default colour, and the load path then
	// persisted that default over the real one.
	const pendingColorRef = useRef(null);
	// Which loadSide run owns the canvas. Every run takes the next number and checks
	// it is still the current one after each await; a run that has been overtaken drops
	// out instead of drawing onto a canvas the newer run has already cleared. Undo and
	// redo replay through loadSide, and holding ctrl+z fires far faster than a load
	// takes - two runs then interleaved, blanking the canvas mid-rebuild and leaving
	// duplicate bounding boxes and snap lines behind. Only the newest run matters.
	const loadRunRef = useRef(0);
	// The run that last drew a side all the way to the end. While it trails
	// loadRunRef a load is still in flight, and the canvas holds only as much of
	// the side as has arrived so far - which is not something to save.
	const loadSettledRef = useRef(0);
	const loadInFlight = () => loadRunRef.current !== loadSettledRef.current;
	// The same for makeOtherSidesThumbnails, which redraws the other sides one
	// at a time and must not let an older run write a side after a newer one.
	const thumbRunRef = useRef(0);

	// Keep a ref to the latest handleSave so external modules (constructed once) can call it
	const handleSaveRef = useRef(null);
	// Stable wrapper passed to helpers; it forwards to the current handleSave implementation
	const handleSaveWrapper = useCallback((...args) => {
		if (handleSaveRef.current) return handleSaveRef.current(...args);
		return null;
	}, []);

  	const [selectedObject, setSelectedObject] = useState(null);
	// Label floating above the duplicate/delete control the pointer is on, if any.
	const [controlHint, setControlHint] = useState(null);
	// Where the toolbar for the selected image sits, or null when there is none.
	const [elementToolbar, setElementToolbar] = useState(null);
	// True while the element is being dragged, scaled or rotated - the toolbar is
	// taken down for the duration instead of chasing the element around.
	const elementToolbarBusyRef = useRef(false);
	const editorContentRef = useRef(null);
  	const [gridEnabled, setGridEnabled] = useState(false);
  	const [gridSize, setGridSize] = useState(20);
  	const gridEnabledRef = useRef(gridEnabled);
  	const gridSizeRef = useRef(gridSize);
	const [colorsOpen, setColorsOpen] = useState(false);
	const [loadingFPDData, setLoadingFPDData] = useState(false);
	// What the customer is told about loading the editor: 'slow' while a request
	// it cannot work without is being retried, 'failed' once that has given up.
	// 'failed' blocks the editor - without bounding boxes artwork could be placed
	// outside the print area. Being offline is tracked apart, as it can happen
	// long after everything has loaded.
	const [connectionIssue, setConnectionIssue] = useState(null);
	const [isOffline, setIsOffline] = useState(navigator.onLine === false);
	// Set by the mount effect: reruns whichever essential loads have failed.
	const retryEssentialLoadsRef = useRef(null);
	const pinchState = useRef({});
	const [cropBoxDone, setCropBoxDone] = useState(false);
	// Set while the parent's text input has focus on mobile: the shirt zoom to go
	// back to afterwards, and the band of the iframe the keyboard leaves visible.
	const textFocusRef = useRef(null);

	// Undo/Redo state
	const [history, setHistory] = useState([]);
	const [redoStack, setRedoStack] = useState([]);
	const isRestoringHistory = useRef(false);
	
	const [cursorInBoundingBox, setCursorInBoundingBox] = useState(false);

	const [showBoundingBoxAddBtnTimer, setShowBoundingBoxAddBtnTimer] = useState(null);
	const showBoundingBoxAddBtnTimerRef = useRef(showBoundingBoxAddBtnTimer);
	useEffect(() => { showBoundingBoxAddBtnTimerRef.current = showBoundingBoxAddBtnTimer; }, [showBoundingBoxAddBtnTimer]);

	//let showBoundingBoxAddBtnTimer = null;
	
	let boundingBoxAddBtn = null;
	// The same object again. The binding above is assigned from inside the mount
	// effect, so only closures created at mount can see it - anything built on a
	// later render, such as an event handler in the JSX, reads null.
	const boundingBoxAddBtnRef = useRef(null);
	// Where the pointer was when the add cursor was last taken down, in client
	// coordinates. Held until the pointer really moves - see hideBoundingBoxAddCursor.
	const addCursorHoldRef = useRef(null);
	// True from the click on a print area that asks the parent for its menu until
	// that menu is done with: the parent has run the action it offered, told us it
	// closed, or the customer has pressed on the canvas again. The add cursor
	// stays down the whole time - the customer is already in the middle of an
	// action, and a "+" inviting them to start another has nothing to add.
	const menuOpenRef = useRef(false);

	const deleteIcon =
	  "data:image/svg+xml,%3Csvg%20width%3D%22165%22%20height%3D%22165%22%20viewBox%3D%220%200%2033%2033%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cg%20filter%3D%22url%28%23filter0_d_49_7034%29%22%3E%0A%3Crect%20x%3D%224%22%20width%3D%2225%22%20height%3D%2225%22%20rx%3D%2212.5%22%20fill%3D%22white%22%2F%3E%0A%3C%2Fg%3E%0A%3Cpath%20d%3D%22M14.9206%206C14.7494%206%2014.6111%206.13825%2014.6111%206.30952C14.6111%206.48079%2014.7494%206.61905%2014.9206%206.61905H18.0159C18.1871%206.61905%2018.3254%206.48079%2018.3254%206.30952C18.3254%206.13825%2018.1871%206%2018.0159%206H14.9206ZM12.3413%207.44444C11.6015%207.44444%2011%208.04595%2011%208.78571C11%209.52548%2011.6015%2010.127%2012.3413%2010.127H20.2857V16.627C20.2857%2017.5937%2019.4985%2018.381%2018.5317%2018.381H14.4048C13.438%2018.381%2012.6508%2017.5937%2012.6508%2016.627V11.2619C12.6508%2011.0906%2012.5125%2010.9524%2012.3413%2010.9524C12.17%2010.9524%2012.0317%2011.0906%2012.0317%2011.2619V16.627C12.0317%2017.9352%2013.0965%2019%2014.4048%2019H18.5317C19.84%2019%2020.9048%2017.9352%2020.9048%2016.627V10.0899C21.4949%209.94959%2021.9365%209.41921%2021.9365%208.78571C21.9365%208.04595%2021.335%207.44444%2020.5952%207.44444H12.3413ZM12.3413%208.06349H20.5952C20.9935%208.06349%2021.3175%208.38746%2021.3175%208.78571C21.3175%209.18397%2020.9935%209.50794%2020.5952%209.50794H12.3413C11.943%209.50794%2011.619%209.18397%2011.619%208.78571C11.619%208.38746%2011.943%208.06349%2012.3413%208.06349ZM15.0238%2011.5714C14.8525%2011.5714%2014.7143%2011.7097%2014.7143%2011.881V16.627C14.7143%2016.7983%2014.8525%2016.9365%2015.0238%2016.9365C15.1951%2016.9365%2015.3333%2016.7983%2015.3333%2016.627V11.881C15.3333%2011.7097%2015.1951%2011.5714%2015.0238%2011.5714ZM17.9127%2011.5714C17.7414%2011.5714%2017.6032%2011.7097%2017.6032%2011.881V16.627C17.6032%2016.7983%2017.7414%2016.9365%2017.9127%2016.9365C18.084%2016.9365%2018.2222%2016.7983%2018.2222%2016.627V11.881C18.2222%2011.7097%2018.084%2011.5714%2017.9127%2011.5714Z%22%20fill%3D%22%23D2042D%22%2F%3E%0A%3Cdefs%3E%0A%3Cfilter%20id%3D%22filter0_d_49_7034%22%20x%3D%220%22%20y%3D%220%22%20width%3D%2233%22%20height%3D%2233%22%20filterUnits%3D%22userSpaceOnUse%22%20color-interpolation-filters%3D%22sRGB%22%3E%0A%3CfeFlood%20flood-opacity%3D%220%22%20result%3D%22BackgroundImageFix%22%2F%3E%0A%3CfeColorMatrix%20in%3D%22SourceAlpha%22%20type%3D%22matrix%22%20values%3D%220%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%20127%200%22%20result%3D%22hardAlpha%22%2F%3E%0A%3CfeOffset%20dy%3D%224%22%2F%3E%0A%3CfeGaussianBlur%20stdDeviation%3D%222%22%2F%3E%0A%3CfeComposite%20in2%3D%22hardAlpha%22%20operator%3D%22out%22%2F%3E%0A%3CfeColorMatrix%20type%3D%22matrix%22%20values%3D%220%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200.25%200%22%2F%3E%0A%3CfeBlend%20mode%3D%22normal%22%20in2%3D%22BackgroundImageFix%22%20result%3D%22effect1_dropShadow_49_7034%22%2F%3E%0A%3CfeBlend%20mode%3D%22normal%22%20in%3D%22SourceGraphic%22%20in2%3D%22effect1_dropShadow_49_7034%22%20result%3D%22shape%22%2F%3E%0A%3C%2Ffilter%3E%0A%3C%2Fdefs%3E%0A%3C%2Fsvg%3E%0A";

	const cloneIcon =
	  "data:image/svg+xml,%3Csvg%20width%3D%22165%22%20height%3D%22165%22%20viewBox%3D%220%200%2033%2033%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cg%20filter%3D%22url%28%23filter0_d_49_7047%29%22%3E%0A%3Crect%20x%3D%224%22%20width%3D%2225%22%20height%3D%2225%22%20rx%3D%2212.5%22%20fill%3D%22white%22%2F%3E%0A%3C%2Fg%3E%0A%3Cpath%20d%3D%22M18.5654%205.9248C19.0954%205.92485%2019.6064%206.13367%2019.9824%206.50391L21.4863%207.98535L21.4873%207.98633C21.8623%208.36522%2022.0752%208.85928%2022.0752%209.38281V14.667C22.075%2015.9069%2021.056%2016.9082%2019.7998%2016.9082H19.3232C19.2831%2018.1133%2018.2834%2019.075%2017.0527%2019.0752H13.2002C11.9438%2019.0752%2010.9248%2018.0731%2010.9248%2016.833V10.333C10.925%209.09304%2011.9439%208.0918%2013.2002%208.0918H13.6768C13.7168%206.88668%2014.7165%205.92504%2015.9473%205.9248H18.5654ZM13.2002%208.78125C12.3309%208.78125%2011.6222%209.47974%2011.6221%2010.333V16.833C11.6221%2017.6864%2012.3308%2018.3857%2013.2002%2018.3857H17.043C17.8867%2018.3857%2018.5762%2017.7272%2018.6162%2016.9082H15.9473C14.6912%2016.908%2013.6731%2015.9068%2013.6729%2014.667V8.78125H13.2002ZM15.9473%206.61426C15.0781%206.6145%2014.3701%207.31371%2014.3701%208.16699V14.667C14.3703%2015.5201%2015.0783%2016.2185%2015.9473%2016.2188H19.7998C20.6591%2016.2188%2021.3598%2015.5361%2021.376%2014.6963L21.3682%2014.6895V9.37305C21.3681%209.35893%2021.3657%209.33978%2021.3643%209.32031H19.7998C19.1464%209.32026%2018.6201%208.7935%2018.6201%208.15723V6.61523C18.6179%206.61513%2018.6156%206.61533%2018.6133%206.61523C18.6002%206.61471%2018.5879%206.61438%2018.5791%206.61426H15.9473ZM19.3174%208.16699C19.3176%208.41767%2019.5259%208.63086%2019.791%208.63086H21.1201C21.0867%208.58359%2021.0491%208.53906%2021.0098%208.49805H21.0039L20.9824%208.47656L19.4785%206.99512C19.4307%206.94807%2019.3747%206.90525%2019.3174%206.86621V8.16699Z%22%20fill%3D%22black%22%20stroke%3D%22black%22%20stroke-width%3D%220.15%22%2F%3E%0A%3Cdefs%3E%0A%3Cfilter%20id%3D%22filter0_d_49_7047%22%20x%3D%220%22%20y%3D%220%22%20width%3D%2233%22%20height%3D%2233%22%20filterUnits%3D%22userSpaceOnUse%22%20color-interpolation-filters%3D%22sRGB%22%3E%0A%3CfeFlood%20flood-opacity%3D%220%22%20result%3D%22BackgroundImageFix%22%2F%3E%0A%3CfeColorMatrix%20in%3D%22SourceAlpha%22%20type%3D%22matrix%22%20values%3D%220%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%20127%200%22%20result%3D%22hardAlpha%22%2F%3E%0A%3CfeOffset%20dy%3D%224%22%2F%3E%0A%3CfeGaussianBlur%20stdDeviation%3D%222%22%2F%3E%0A%3CfeComposite%20in2%3D%22hardAlpha%22%20operator%3D%22out%22%2F%3E%0A%3CfeColorMatrix%20type%3D%22matrix%22%20values%3D%220%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200.25%200%22%2F%3E%0A%3CfeBlend%20mode%3D%22normal%22%20in2%3D%22BackgroundImageFix%22%20result%3D%22effect1_dropShadow_49_7047%22%2F%3E%0A%3CfeBlend%20mode%3D%22normal%22%20in%3D%22SourceGraphic%22%20in2%3D%22effect1_dropShadow_49_7047%22%20result%3D%22shape%22%2F%3E%0A%3C%2Ffilter%3E%0A%3C%2Fdefs%3E%0A%3C%2Fsvg%3E%0A";
	
	const scaleIcon =
	  "data:image/svg+xml,%3Csvg%20width%3D%22165%22%20height%3D%22165%22%20viewBox%3D%220%200%2033%2033%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cg%20filter%3D%22url%28%23filter0_d_49_7038%29%22%3E%0A%3Crect%20x%3D%224%22%20width%3D%2225%22%20height%3D%2225%22%20rx%3D%2212.5%22%20fill%3D%22white%22%2F%3E%0A%3C%2Fg%3E%0A%3Cg%20clip-path%3D%22url%28%23clip0_49_7038%29%22%3E%0A%3Cpath%20d%3D%22M15.1067%2011.1072L10.4639%206.46436%22%20stroke%3D%22%23000001%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3Cpath%20d%3D%22M14.1782%206.46436H10.4639V10.1786%22%20stroke%3D%22%23000001%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3Cpath%20d%3D%22M17.8926%2013.8931L22.5354%2018.5359%22%20stroke%3D%22%23000001%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3Cpath%20d%3D%22M18.8213%2018.5356H22.5356V14.8213%22%20stroke%3D%22%23000001%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3C%2Fg%3E%0A%3Cdefs%3E%0A%3Cfilter%20id%3D%22filter0_d_49_7038%22%20x%3D%220%22%20y%3D%220%22%20width%3D%2233%22%20height%3D%2233%22%20filterUnits%3D%22userSpaceOnUse%22%20color-interpolation-filters%3D%22sRGB%22%3E%0A%3CfeFlood%20flood-opacity%3D%220%22%20result%3D%22BackgroundImageFix%22%2F%3E%0A%3CfeColorMatrix%20in%3D%22SourceAlpha%22%20type%3D%22matrix%22%20values%3D%220%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%20127%200%22%20result%3D%22hardAlpha%22%2F%3E%0A%3CfeOffset%20dy%3D%224%22%2F%3E%0A%3CfeGaussianBlur%20stdDeviation%3D%222%22%2F%3E%0A%3CfeComposite%20in2%3D%22hardAlpha%22%20operator%3D%22out%22%2F%3E%0A%3CfeColorMatrix%20type%3D%22matrix%22%20values%3D%220%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200.25%200%22%2F%3E%0A%3CfeBlend%20mode%3D%22normal%22%20in2%3D%22BackgroundImageFix%22%20result%3D%22effect1_dropShadow_49_7038%22%2F%3E%0A%3CfeBlend%20mode%3D%22normal%22%20in%3D%22SourceGraphic%22%20in2%3D%22effect1_dropShadow_49_7038%22%20result%3D%22shape%22%2F%3E%0A%3C%2Ffilter%3E%0A%3CclipPath%20id%3D%22clip0_49_7038%22%3E%0A%3Crect%20width%3D%2213%22%20height%3D%2213%22%20fill%3D%22white%22%20transform%3D%22translate%2810%206%29%22%2F%3E%0A%3C%2FclipPath%3E%0A%3C%2Fdefs%3E%0A%3C%2Fsvg%3E%0A";

	const rotateIcon =
	  "data:image/svg+xml,%3Csvg%20width%3D%22165%22%20height%3D%22165%22%20viewBox%3D%220%200%2033%2033%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cg%20filter%3D%22url%28%23filter0_d_49_7031%29%22%3E%0A%3Crect%20x%3D%224%22%20width%3D%2225%22%20height%3D%2225%22%20rx%3D%2212.5%22%20fill%3D%22white%22%2F%3E%0A%3C%2Fg%3E%0A%3Cpath%20d%3D%22M12.1692%207.63514C12.1692%207.63514%2012.1583%207.63514%2012.1473%207.63514C12.1473%207.33092%2012.1473%207.01585%2012.1473%206.70076C12.1473%206.5052%2012.1254%206.36393%2012.0817%206.27701C11.9289%205.98366%2011.5136%205.90759%2011.2515%206.12489C11.1205%206.23354%2011.0553%206.3857%2011.0553%206.58127C11.0553%207.51565%2011.0553%208.23275%2011.0553%208.76514C11.0553%209.18887%2011.2516%209.50393%2011.6339%209.68864C11.7758%209.76469%2011.9289%209.79726%2012.0817%209.79726C12.1582%209.79726%2012.9008%209.79726%2014.2876%209.79726C14.7354%209.79726%2015.0299%209.30841%2014.7569%208.92814C14.5821%208.68911%2014.386%208.69988%2014.0584%208.69988C13.2284%208.69988%2012.7586%208.69988%2012.6603%208.69988C12.6603%208.69988%2012.6496%208.70001%2012.6496%208.68914C12.6496%208.68914%2012.6496%208.68913%2012.6496%208.67827C13.283%208.08069%2013.9489%207.65692%2014.6588%207.41789C15.86%206.99416%2017.0612%206.98326%2018.2515%207.38526C19.3108%207.74381%2020.1843%208.36307%2020.8504%209.24313C21.309%209.84071%2021.6256%2010.5145%2021.8112%2011.2641C22.2699%2013.1438%2021.6475%2015.1537%2020.2388%2016.4792C19.2123%2017.4462%2017.8582%2017.9351%2016.4386%2017.946C15.6633%2017.946%2014.7791%2017.7178%2014.1021%2017.3919C13.6216%2017.1637%2013.1624%2016.8378%2012.7584%2016.4684C11.8302%2015.5774%2011.2738%2014.4909%2011.099%2013.198C11.0663%2012.9698%2011.0334%2012.8395%2011.0116%2012.7851C10.826%2012.4918%2010.4544%2012.4048%2010.1814%2012.6439C10.0067%2012.796%209.98519%2012.959%2010.007%2013.2089C10.1927%2014.8712%2011.0006%2016.3924%2012.2673%2017.4571C12.999%2018.0764%2013.8396%2018.511%2014.7787%2018.7717C15.3466%2018.9239%2015.9038%2019%2016.4717%2019C16.9849%2019%2017.5637%2018.9348%2018.0878%2018.8044C19.0488%2018.5545%2019.9004%2018.1199%2020.643%2017.5115C21.1562%2017.0878%2021.6039%2016.5879%2021.9643%2016.0337C22.8379%2014.6865%2023.1872%2013.0024%2022.9033%2011.4161C22.6412%209.93849%2021.9643%208.71086%2020.8723%207.72214C19.5728%206.54872%2017.7819%205.90765%2016.0238%206.04889C14.626%206.15754%2013.2285%206.7225%2012.1692%207.65689V7.63514Z%22%20fill%3D%22black%22%2F%3E%0A%3Cdefs%3E%0A%3Cfilter%20id%3D%22filter0_d_49_7031%22%20x%3D%220%22%20y%3D%220%22%20width%3D%2233%22%20height%3D%2233%22%20filterUnits%3D%22userSpaceOnUse%22%20color-interpolation-filters%3D%22sRGB%22%3E%0A%3CfeFlood%20flood-opacity%3D%220%22%20result%3D%22BackgroundImageFix%22%2F%3E%0A%3CfeColorMatrix%20in%3D%22SourceAlpha%22%20type%3D%22matrix%22%20values%3D%220%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%20127%200%22%20result%3D%22hardAlpha%22%2F%3E%0A%3CfeOffset%20dy%3D%224%22%2F%3E%0A%3CfeGaussianBlur%20stdDeviation%3D%222%22%2F%3E%0A%3CfeComposite%20in2%3D%22hardAlpha%22%20operator%3D%22out%22%2F%3E%0A%3CfeColorMatrix%20type%3D%22matrix%22%20values%3D%220%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200.25%200%22%2F%3E%0A%3CfeBlend%20mode%3D%22normal%22%20in2%3D%22BackgroundImageFix%22%20result%3D%22effect1_dropShadow_49_7031%22%2F%3E%0A%3CfeBlend%20mode%3D%22normal%22%20in%3D%22SourceGraphic%22%20in2%3D%22effect1_dropShadow_49_7031%22%20result%3D%22shape%22%2F%3E%0A%3C%2Ffilter%3E%0A%3C%2Fdefs%3E%0A%3C%2Fsvg%3E%0A";
	  const deleteImg = document.createElement('img');
	deleteImg.src = deleteIcon;

	const cloneImg = document.createElement('img');
	cloneImg.src = cloneIcon;

	const scaleImg = document.createElement('img');
	scaleImg.src = scaleIcon;
	
	const rotateImg = document.createElement('img');
	rotateImg.src = rotateIcon;

	// Both endpoints are rewritten from the print area's bounds every time a guide
	// is shown, so the coordinates here only have to be legal, not meaningful.
	const snapLineHorizontal = new fabric.Line([0, 0, 0, 0], { id: 'snapLineHorizontal', stroke: 'black', selectable: false, evented: false, visible: false, strokeDashArray: [5, 2] });
	const snapLineVertical = new fabric.Line([0, 0, 0, 0], { id: 'snapLineVertical', stroke: 'black', selectable: false, evented: false, visible: false, strokeDashArray: [5, 2] });
	
	//const cursorAdd = 'url("data:image/svg+xml,'+encodeURIComponent('<svg width="37" height="37" viewBox="0 0 37 37" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="36" height="36" rx="18" fill="white"/><rect x="0.5" y="0.5" width="36" height="36" rx="18" stroke="#DEE1EA"/><path d="M18.2656 9.33496V27.1958" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M9.33496 18.2109H27.1958" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>')+') 16 16, auto';
	
	// Set Fabric.js selection and control styles globally
	fabric.Object.prototype.transparentCorners = false;
	fabric.Object.prototype.cornerColor = '#D8127D';
	fabric.Object.prototype.cornerStrokeColor = '#fff';
	fabric.Object.prototype.cornerStyle = 'circle';
	fabric.Object.prototype.borderColor = 'white';
	fabric.Object.prototype.borderScaleFactor = 2;
	// Mirroring is still not allowed, but refusing it is not fabric's job here.
	// With the lock on, scaleObject returns false the moment the sign of either
	// axis flips - and false means the whole transform did nothing, on both axes,
	// for the rest of the drag. Crossing the anchor is easy to do: the anchor of a
	// bottom-right resize is the top-left corner, which on a shirt-sized element is
	// only a few tens of pixels up, right where the duplicate and delete icons sit.
	// So a resize died as soon as the pointer wandered over them and the customer
	// had to let go and start again.
	//
	// Nothing mirrors without the lock either: the object:scaling handler folds a
	// negative scale back to positive through clampScale before anything downstream
	// measures the object. The difference is that the gesture stays alive.
	fabric.Object.prototype.lockScalingFlip = false;
	//fabric.Object.prototype.borderDashArray = [4, 2];
	if(window.innerWidth < 600){
		fabric.Object.prototype.cornerSize = 32;
	}else{
		fabric.Object.prototype.cornerSize = 25;
	}
	fabric.Object.prototype.padding = 16; // Use zero padding so hit area matches visual bounds
	//fabric.Object.prototype.selectionBackgroundColor = 'rgba(216,18,125,0.08)';
	fabric.textureSize = 4096; // Increase texture size for better quality on high-DPI screens

	// Text is only baked into fontSize on object:scaled, so the whole drag runs on
	// scaleX/scaleY. Fabric's default noScaleCache skips the cache re-render while a
	// scale transform is live and stretches the cached bitmap instead, which reads as
	// the glyphs going blurry mid-drag and snapping back sharp on release. Textbox
	// already overrides this to false; fabric.Text does not, so do it here. Re-rendering
	// a text cache per frame is cheap - it is a glyph run, not an image resample.
	fabric.Text.prototype.noScaleCache = false;

	// Region (union of the bounding boxes) for the side currently being edited.
	function getRegion() {
		return regionsRef.current[currentSideRef.current] || EMPTY_REGION;
	}

	// True when every part of the object sits inside the union of the boxes.
	// An element may straddle two boxes that touch; it may not cover a gap.
	function isObjectInsideRegion(obj) {
		return isObjectCovered(getRegion(), obj);
	}

	// The same guarantee the gestures give, applied to elements that did not
	// arrive through one: a design restored from the database, an undo, a side
	// switch, a product whose print areas were moved in the admin after the
	// design was saved. Without it a stored element that is outside the region
	// stays outside for as long as nobody touches it - and gets printed that way.
	//
	// Only elements that are actually outside are moved, so a design that is fine
	// is left exactly as it was drawn. Returns whether anything had to be put back.
	// `side` for callers that know which one they have just drawn - loadSide runs
	// before currentSideRef has caught up with the state change that sent it.
	function enforceAllInRegion(canvas, side) {
		const region = (side && regionsRef.current[side]) || getRegion();
		if (!canvas || typeof canvas.getObjects !== 'function' || region.isEmpty) return false;

		let repaired = false;
		for (const obj of canvas.getObjects()) {
			if (obj.type !== 'text' && obj.type !== 'image') continue;
			if (!isObjectCovered(region, obj)) {
				enforceObjectInRegion(region, obj);
				repaired = true;
			}
			if (isObjectCovered(region, obj)) obj.__lastValidPose = capturePose(obj);
		}
		if (repaired && !canvas.__drawScheduled) canvas.requestRenderAll();
		return repaired;
	}

	// Refit an object after a programmatic change - a new font size, family or
	// weight resizes it with no drag behind it.
	//
	// This is the one path that still caps a size outright, and it is the right
	// one to: a typed font size that does not fit has no gesture behind it to flag
	// and no release to snap on, so it is answered immediately with the largest
	// size that does fit. A resize the customer drags is not capped at all - see
	// handleObjectScaling.
	function fitObjectAfterEdit(obj) {
		const region = getRegion();
		if (!obj || region.isEmpty) return false;

		const c = obj.getCenterPoint();
		const anchor = { x: c.x, y: c.y, originX: 'center', originY: 'center' };
		const { clamped } = capObjectScale(region, obj, anchor, 0);

		// Text that outgrows the print area is shrunk, not refused: the cap is
		// baked into fontSize (so text stays at scale 1 and the size we report
		// back is the size actually on the canvas) and trimmed down to
		// MIN_FONT_SIZE. Only when even that does not fit is the element locked -
		// which is what the parent's input reacts to, so typing keeps working all
		// the way down to the floor.
		//
		// The cap alone does not settle it. It only ever resizes about the centre,
		// and baking the result can come out slightly bigger than the size it
		// validated, so a text sitting against an edge could be left poking out of
		// it with nothing to move it back. enforceObjectInRegion does move it.
		const covered = enforceObjectInRegion(region, obj);
		// Images have no font size to trim, so for them the cap is the answer and
		// being capped is what the parent is told about.
		const locked = obj.type === 'text' ? !covered : clamped;

		obj.clamped = locked;
		window.parent.postMessage({ action: 'scalingLocked', value: locked }, '*');
		if (isObjectInsideRegion(obj)) obj.__lastValidPose = capturePose(obj);
		// A font size typed in the parent resizes the element with no drag behind
		// it, so this is the other place the haloes can go stale.
		refreshControlHitAreas(obj, controlCornerSize());
		return locked;
	}

	// Regions for the current side - a region is one or more boxes that overlap
	// or touch, and it is the unit the insert UI talks about.
	function currentBoxGroups() {
		return boxGroupsRef.current[currentSideRef.current] || [];
	}

	// Which box receives an element inserted at this point. Boxes drawn as one
	// region behave as one box here too, so the insert lands in the box the
	// region's add button sits in rather than whichever piece is under the
	// cursor. Null when the point is outside every box.
	function insertBoxAt(x, y) {
		const group = groupAtPoint(currentBoxGroups(), x, y);
		return group ? group.largest : null;
	}

	// --- magnetic centring ----------------------------------------------------
	//
	// The two dashed guides that appear while a dragged element is centred in its
	// print area. They are looked up on the canvas by id rather than held onto:
	// loadSide clears the canvas and adds a fresh pair on every side switch, so
	// the instance on screen is not necessarily the one this closure created.
	function snapLine(id) {
		const canvas = canvasRef.current;
		if (!canvas) return null;
		return canvas.getObjects().find(o => o.id === id) || null;
	}

	// Put a guide on `position` (the x of the vertical line, the y of the
	// horizontal one), or take it down when the axis did not catch. The guide is
	// the centre line of `bounds` and stops at its edges rather than running the
	// width of the canvas: it marks the centre of one print area, and a line
	// crossing the neighbouring areas as well reads as if it centred those too.
	//
	// Setting x1/y1/x2/y2 is what moves a fabric Line - it recomputes width,
	// height, left and top from them - so there is no left/top to set here.
	// Returns whether anything about it changed, so a frame with no snapping asks
	// for no redraw.
	function setSnapLine(id, position, bounds) {
		const line = snapLine(id);
		if (!line) return false;

		if (position == null || !bounds) {
			if (!line.visible) return false;
			line.set({ visible: false });
			return true;
		}

		const wasVisible = line.visible;
		line.set(id === 'snapLineVertical'
			? { x1: position, y1: bounds.y0, x2: position, y2: bounds.y1, visible: true }
			: { x1: bounds.x0, y1: position, x2: bounds.x1, y2: position, visible: true });
		// The lines are added before the design is, so without this they would be
		// drawn underneath the element they are guiding. Only worth doing as the
		// guide appears, not on every frame of the drag.
		if (!wasVisible) line.bringToFront();
		return true;
	}

	// Pull the centre of a dragged element onto the centre of its print area,
	// horizontally, vertically or both, and show the guide for whichever axis
	// caught. The pull is measured against the position the drag asked for - the
	// centre fabric has just written - so the element lets go again as soon as
	// the pointer leaves the threshold, and stays put in between.
	function applyCenterSnap(obj) {
		const canvas = canvasRef.current;
		if (!obj || !canvas) return;

		const center = obj.getCenterPoint();
		const bounds = snapBoundsFor(currentBoxGroups(), center);
		const snapped = centerSnap(center, bounds, SNAP_THRESHOLD);

		if (snapped.x != null || snapped.y != null) {
			const wasInside = isObjectInsideRegion(obj);
			obj.setPositionByOrigin({
				x: snapped.x != null ? snapped.x : center.x,
				y: snapped.y != null ? snapped.y : center.y
			}, 'center', 'center');
			obj.setCoords();

			// The centre of a region is the centre of the rectangle around it, and
			// an L-shaped region - boxes that touch along part of an edge - has a
			// corner missing there. The magnet may not be the thing that drags an
			// element out of the print area: where it would, the drag keeps the
			// position it asked for and no guide is shown.
			if (wasInside && !isObjectInsideRegion(obj)) {
				obj.setPositionByOrigin(center, 'center', 'center');
				obj.setCoords();
				snapped.x = null;
				snapped.y = null;
			}
		}

		let changed = setSnapLine('snapLineVertical', snapped.x, bounds);
		if (setSnapLine('snapLineHorizontal', snapped.y, bounds)) changed = true;
		if (changed && !canvas.__drawScheduled) canvas.requestRenderAll();
	}

	// The drag is over: the guides have nothing left to say. Also the one thing
	// standing between a guide and a downloaded design with a dashed line across
	// it, for a drag that ended somewhere object:moved never reported.
	function hideSnapLines() {
		const canvas = canvasRef.current;
		if (!canvas) return;
		let changed = setSnapLine('snapLineVertical', null);
		if (setSnapLine('snapLineHorizontal', null)) changed = true;
		if (changed && !canvas.__drawScheduled) canvas.requestRenderAll();
	}

	// Which controls sit on a corner of the element, and so have a neighbour close
	// enough to collide with. The rotate handle is not one of them: it floats below
	// the element, in space nothing else is drawn in.
	const CORNER_CONTROLS = ['deleteControl', 'cloneControl', 'scaleControl'];

	// Invisible slop around every icon. Fabric hit-tests a control against
	// sizeX/sizeY (mouse) and touchSizeX/touchSizeY (touch), which fall back to the
	// object's cornerSize / touchCornerSize - 25 and 24px here, i.e. a square
	// *smaller* than the icon we draw at cornerSize. Hitting a control meant
	// landing dead centre on it. These widen only the hit test; nothing about the
	// rendered icon changes, since renderIcon draws at cornerSize.
	//
	// Capped at the distance to the neighbouring control, which for a corner
	// control is the element's own width or height: a halo wider than the element
	// reaches across the corner opposite it, and then one point belongs to two
	// controls at once. Per axis, so a wide, short element keeps its generous
	// horizontal slop and gives up only the vertical.
	//
	// Never below cornerSize - the hit area is not allowed to be smaller than the
	// icon that is drawn. On an element smaller than an icon the haloes do still
	// overlap, and fabric then takes the last control in insertion order, which is
	// why they are added scale, then clone, then delete: the destructive one never
	// steals a grab from its neighbour.
	function controlHitArea(obj, cornerSize, capToObject) {
		const canvas = canvasRef.current;
		const zoom = (canvas && typeof canvas.getZoom === 'function' && canvas.getZoom()) || 1;
		// The controls sit padding clear of the element on every side, so two on
		// opposite corners are the element plus twice the padding apart - not the
		// element. Measuring without it gave away 2 * padding of slop for nothing.
		// oCoords, and so the haloes, live in viewport pixels.
		const spread = 2 * (obj.padding || 0);
		const width = capToObject && obj.getScaledWidth ? (obj.getScaledWidth() + spread) * zoom : Infinity;
		const height = capToObject && obj.getScaledHeight ? (obj.getScaledHeight() + spread) * zoom : Infinity;

		const fit = (slop, spacing) => Math.max(cornerSize, Math.min(cornerSize + slop, spacing));
		return {
			sizeX: fit(24, width),
			sizeY: fit(24, height),
			touchSizeX: fit(32, width),
			touchSizeY: fit(32, height),
		};
	}

	// The delete control gets no halo: a stray tap next to it should not throw an
	// element away, so only the icon itself counts. Set explicitly, because without
	// these fabric falls back to the smaller cornerSize of the object.
	function deleteHitArea(cornerSize) {
		return { sizeX: cornerSize, sizeY: cornerSize, touchSizeX: cornerSize, touchSizeY: cornerSize };
	}

	// The haloes are measured off the element, so a resize has to bring them with
	// it. Fabric re-reads sizeX/sizeY on every setCoords, so assigning them is
	// enough - there is nothing to invalidate.
	function refreshControlHitAreas(obj, cornerSize) {
		if (!obj || !obj.controls) return;
		const hitArea = controlHitArea(obj, cornerSize, true);
		CORNER_CONTROLS.forEach(key => {
			if (obj.controls[key]) Object.assign(obj.controls[key], hitArea, { cornerSize });
		});
		if (obj.controls.deleteControl) Object.assign(obj.controls.deleteControl, deleteHitArea(cornerSize));
		if (obj.controls.mtr) {
			obj.controls.mtr.cornerSize = cornerSize;
			obj.controls.mtr.offsetY = rotateControlOffset(cornerSize);
		}
	}

	// How far the rotate handle sits below the bottom edge. On mobile the icons are
	// far bigger in canvas pixels than the desktop 40, which left the handle level
	// with the scale icon: on a narrow element the two collided however tall it
	// was. A full spacing down, it clears the scale icon at any width.
	function rotateControlOffset(cornerSize) {
		return window.innerWidth < 600 ? controlSpacing(cornerSize) : 40;
	}

	// On mobile a resize stops where the corner icons would otherwise come too
	// close together; dragging back out grows the element again, because fabric
	// works the scale out from the pointer anew on every frame. Runs mid-gesture,
	// after fabric has already pinned the anchor, so the anchor is put back by
	// hand. Worked out as a factor on the current size, because an image can
	// carry a different scale per axis and has to keep its proportions.
	function keepControlsApart(obj, transform, cornerSize) {
		if (window.innerWidth >= 600) return;

		const canvas = canvasRef.current;
		const floor = minScaleForSpacing({
			width: obj.getScaledWidth(),
			height: obj.getScaledHeight(),
			padding: obj.padding,
			zoom: canvas.getZoom()
		}, controlSpacing(cornerSize));

		const grabbed = transform && transform.original;
		const factor = flooredScale(1, floor, grabbed && grabbed.scaleX / obj.scaleX);
		if (factor === 1) return;

		const originX = transform ? transform.originX : 'center';
		const originY = transform ? transform.originY : 'center';
		const anchor = obj.translateToOriginPoint(obj.getCenterPoint(), originX, originY);
		obj.set({ scaleX: obj.scaleX * factor, scaleY: obj.scaleY * factor });
		obj.setPositionByOrigin(anchor, originX, originY);
		obj.setCoords();
	}

	// On mobile the button - the 25px circle inside the 33px icon art - is 25 CSS
	// pixels on screen.
	const MOBILE_ICON_PX = 40;

	// The corner size the controls on this object were built at. cornerSize is in
	// canvas pixels, and on mobile the canvas is drawn far smaller than its 950px
	// and then zoomed by the crop transform, so a fixed number came out at
	// whatever size that happened to give. The container's on-screen width
	// includes the transform.
	// shownWidth can be passed in by a zoom that has only just been set: while it
	// animates, the measured width is still the old one.
	function controlCornerSize(shownWidthOverride) {
		if (window.innerWidth >= 600) return 40;
		const canvas = canvasRef.current;
		const container = document.querySelector('div.canvas-container');
		const shownWidth = shownWidthOverride || (container && container.getBoundingClientRect().width);
		if (!canvas || !shownWidth) return 48;
		return MOBILE_ICON_PX * canvas.width / shownWidth;
	}

	// Helper to set custom controls on an object
	function setCustomControls(obj, shownWidth) {
		let offset = 0;
		const cornerSize = controlCornerSize(shownWidth);
		if (window.innerWidth < 600) {
			offset = 32;
		}

		const hitArea = controlHitArea(obj, cornerSize, true);
		// The rotate handle sits clear of the bottom edge, so its neighbours
		// are a fixed distance away rather than an element-sized one, and it keeps
		// the full slop. It is also first in insertion order and so lowest in
		// priority: its halo can never take a grab off one of the corners.
		const rotateHitArea = controlHitArea(obj, cornerSize, false);

		// object:added routes every object here, so this is the one place that
		// catches elements added after the load as well - a text or image dropped on
		// a white shirt would otherwise keep the white border it inherits from the
		// prototype until the next reload.
		obj.borderColor = selectionBorderColorRef.current;

		// Hide all default scale handles except bottom-right (scale) and rotation
		obj.setControlsVisibility({
			tl: false,
			tr: false,
			bl: false,
			ml: false,
			mr: false,
			mt: false,
			mb: false,
			br: false,  // scale
			mtr: true, // rotate
		});

		// Top right: delete
		obj.controls.deleteControl = new fabric.Control({
			x: 0.5,
			y: -0.5,
			cursorStyle: 'pointer',
			...clickControl('delete', deleteObject),
			render: renderIcon(deleteImg),
			cornerSize: cornerSize,
			...deleteHitArea(cornerSize),
		});

		// Top left: clone
		obj.controls.cloneControl = new fabric.Control({
			x: -0.5,
			y: -0.5,
			cursorStyle: 'pointer',
			...clickControl('clone', cloneObject),
			render: renderIcon(cloneImg),
			cornerSize: cornerSize,
			...hitArea,
		});

		// Bottom right: scale (handled by default bottom-right control, so we just add an icon)
		obj.controls.scaleControl = new fabric.Control({
			x: 0.5,
			y: 0.5,
			cursorStyle: 'nwse-resize',
			actionHandler: fabric.controlsUtils.scalingEqually,
			render: renderIcon(scaleImg),
			cornerSize: cornerSize,
			...hitArea,
		});

		// Rotation control is handled by default mtr control, so we just add an icon
		obj.controls.mtr.render = renderIcon(rotateImg);
		obj.controls.mtr.cursorStyle = 'grab';
		obj.controls.mtr.cornerSize = cornerSize;
		Object.assign(obj.controls.mtr, rotateHitArea);
		// Below the element instead of fabric's default above it. Mirrors the
		// default: anchored to the bottom edge with a gap below it, which keeps
		// the connecting line - drawn from the anchor to the offset - pointing down.
		obj.controls.mtr.y = 0.5;
		obj.controls.mtr.offsetY = rotateControlOffset(cornerSize);
	}

	// Helper: Save current canvas state to history
	function saveHistory() {
		if (isRestoringHistory.current) return;
		//console.log("Saving history");
		const canvas = canvasRef.current;
		// 'src' has to be listed: on an SVG it is a custom property on the group and
		// undo/redo replays through loadSide, which reloads the file from it.
		const json = canvas.toDatalessJSON(['text', 'url', 'src']);
		json.objects = json.objects.filter(obj => obj.type === 'text' || obj.type === 'image');
		// Undo/redo replays through loadSide, which does img.set(el) - a serialised
		// resizeFilter would land there as a plain object and break the render.
		json.objects.forEach(obj => { delete obj.resizeFilter; });
		setHistory(prev => [...prev, json]);
		window.parent.postMessage({ action: 'undo', value: 1 }, '*');
		setRedoStack([]); // Clear redo stack on new action
		window.parent.postMessage({ action: 'redo', value: 0 }, '*');
	}

	// Undo handler
	function handleUndo() {
		//console.log(history);
		if (history.length < 2){ 
			window.parent.postMessage({ action: 'undo', value: 0 }, '*');
			return;
		}
		const canvas = canvasRef.current;
		const prevHistory = [...history];
		const last = prevHistory.pop();
		const prev = prevHistory[prevHistory.length - 1];
		if(prevHistory.length === 1){
			window.parent.postMessage({ action: 'undo', value: 0 }, '*');
		}
		setHistory(prevHistory);
		//console.log(last);
		setRedoStack(r => [last, ...r]);
		window.parent.postMessage({ action: 'redo', value: 1 }, '*');
		//console.log(redoStack);
		isRestoringHistory.current = true;
		//console.log(prev);
		//canvas.loadFromJSON(prev, () => {
		//	canvas.renderAll();
		//	isRestoringHistory.current = false;
		//});
		const colorObj = findColor(colors, selectedShirtColorRef.current);
		loadSide(canvas, prev.objects || [], currentSideRef.current, productId, colorObj);
	}

	// Redo handler
	function handleRedo() {
		//console.log(redoStack);
		if (redoStack.length === 0){
			window.parent.postMessage({ action: 'redo', value: 0 }, '*');
			return;
		}
		const canvas = canvasRef.current;
		const [next, ...rest] = redoStack;
		if(rest.length === 0){
			window.parent.postMessage({ action: 'redo', value: 0 }, '*');
		}
		setRedoStack(rest);
		setHistory(prev => [...prev, next]);
		window.parent.postMessage({ action: 'undo', value: 1 }, '*');
		isRestoringHistory.current = true;
		//canvas.loadFromJSON(next, () => {
		//	isRestoringHistory.current = false;
		//});
		const colorObj = findColor(colors, selectedShirtColorRef.current);
		loadSide(canvas, next.objects || [], currentSideRef.current, productId, colorObj);
	}

  	useEffect(() => {
    	gridEnabledRef.current = gridEnabled;
  	}, [gridEnabled]);

  	useEffect(() => {
    	gridSizeRef.current = gridSize;
  	}, [gridSize]);

	// Single entry point for the grid when the parent asks for it: state drives
	// the snapping, drawGrid paints it now.
	function applyGrid(enabled, size) {
		const step = size || 20;
		setGridEnabled(enabled);
		if (enabled) setGridSize(step);
		if (gridToolsInstanceRef.current) {
			gridToolsInstanceRef.current.drawGrid(canvasRef.current, enabled, step);
		}
	}

	// Backstop only: every colour change goes through setShirtColor, which moves
	// the ref with the state. This effect cannot be the primary sync - it runs a
	// render too late, and loadSide saves the colour before it gets there.
  	useEffect(() => {
    	selectedShirtColorRef.current = selectedShirtColor;
  	}, [selectedShirtColor]);

	// The colour is read from the ref by everything that runs after an await -
	// most importantly the save at the end of loadSide - so the ref has to move
	// in the same breath as the state, never a render behind it.
	function setShirtColor(color) {
		if (!color) return false;
		selectedShirtColorRef.current = color;
		setSelectedShirtColor(color);
		return true;
	}

	// Same, but refuses a colour from a weaker source than the one already
	// applied. See colorSourceRef.
	function applyShirtColor(color, source) {
		if (!color) return false;
		if (COLOR_SOURCE_RANK[source] < COLOR_SOURCE_RANK[colorSourceRef.current]) return false;
		colorSourceRef.current = source;
		return setShirtColor(color);
	}


	function clearControlHint() {
		// Returning the previous value unchanged lets React skip the re-render,
		// which matters because this runs on every mouse move.
		setControlHint(prev => (prev ? null : prev));
	}

	// The toolbar buttons are DOM elements rather than fabric controls, so their
	// labels are placed from the button's own box. Everything after that - the
	// styling, the gap, the flip to below when there is no room above - is the
	// control label, so the two read as one thing.
	function showElementToolbarHint(button, label) {
		const host = editorContentRef.current;
		if (!host || !button) return;
		const rect = button.getBoundingClientRect();
		const hostRect = host.getBoundingClientRect();
		const left = rect.left - hostRect.left + rect.width / 2;
		const top = rect.top - hostRect.top + rect.height / 2;
		setControlHint({ label, left, top, below: top < CONTROL_HINT_GAP + 44 });
	}

	// The toolbar covers a piece of the canvas, so moving onto it never reaches
	// fabric: the add cursor drawn while the pointer is inside a print area would
	// stay behind, frozen under the toolbar. Take it down on the way in - mouse:move
	// puts it back as soon as the pointer moves over the canvas again.
	//
	// `event` is what took it down - a click on a toolbar button, the pointer
	// arriving on the toolbar - and the position it carries holds the cursor down
	// until the pointer leaves that spot. Pressing a button does not move the
	// pointer, but what follows the press does reach fabric as a mouse:move at the
	// position the pointer is already at: the panel opening, the toolbar going
	// away. Without the hold the "+" comes straight back at the spot it was just
	// taken from, over a panel the customer has only now asked for.
	function hideBoundingBoxAddCursor(event) {
		if (event && event.clientX != null) {
			addCursorHoldRef.current = { x: event.clientX, y: event.clientY };
		}
		const canvas = canvasRef.current;
		const btn = boundingBoxAddBtnRef.current;
		if (!canvas || !btn || !btn.visible) return;
		setCursorInBoundingBox(false);
		btn.set({ visible: false });
		canvas.renderAll();
	}

	// The "Design toevoegen" label in the middle of an empty print area. Every
	// place that puts it up comes through here, so none of them can put it up
	// while the parent's menu is open - the customer is already adding something.
	function showBoundingBoxAddLabels(visible) {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const show = !!visible && !menuOpenRef.current;
		canvas.getObjects()
			.filter(obj => obj.type === 'boundingBoxAddBtn')
			.forEach(label => label.set({ visible: show }));
		canvas.renderAll();
	}

	function designIsEmpty() {
		const side = designsRef.current[currentSideRef.current] || [];
		return !side.some(obj => obj.type === 'image' || obj.type === 'text');
	}

	// The customer has asked the parent for its menu from a print area: nothing
	// on the canvas may invite them to start something else until it is gone.
	function menuOpened(event) {
		menuOpenRef.current = true;
		hideBoundingBoxAddCursor(event);
		showBoundingBoxAddLabels(false);
	}

	// The menu is gone. `restoreLabels` is false when what closed it is about to
	// add an element itself - the label would only flash before that lands.
	function menuClosed(restoreLabels = true) {
		if (!menuOpenRef.current) return;
		menuOpenRef.current = false;
		if (restoreLabels && designIsEmpty()) showBoundingBoxAddLabels(true);
	}

	// True while the pointer is still where it was when the add cursor was taken
	// down. Any other position is the customer moving the mouse, which lets go.
	function addCursorHeldAt(event) {
		const hold = addCursorHoldRef.current;
		if (!hold) return false;
		if (event && hold.x === event.clientX && hold.y === event.clientY) return true;
		addCursorHoldRef.current = null;
		return false;
	}

	// Put the toolbar over the top edge of the selected image. Called from
	// after:render rather than from a list of events, so it follows the element
	// however it got where it is - a drag, an undo, a parent-driven edit.
	function updateElementToolbar() {
		const canvas = canvasRef.current;
		const host = editorContentRef.current;
		const active = canvas && canvas.getActiveObject();
		if (!host || !active || active.type !== 'image' || elementToolbarBusyRef.current) {
			setElementToolbar(prev => (prev ? null : prev));
			return;
		}

		// The same conversion the control label does: canvas coordinates are
		// backing-store pixels, but the canvas is laid out at whatever width fits
		// the viewport, so they have to be scaled to mean anything in the page.
		const bounds = active.getBoundingRect();
		const canvasRect = canvas.upperCanvasEl.getBoundingClientRect();
		const hostRect = host.getBoundingClientRect();
		const scale = canvas.getWidth() ? canvasRect.width / canvas.getWidth() : 1;
		const centerX = canvasRect.left - hostRect.left + (bounds.left + bounds.width / 2) * scale;
		const topEdge = canvasRect.top - hostRect.top + bounds.top * scale;

		// editorContent clips its overflow. An element near an edge keeps its
		// toolbar - pulled back inside rather than dropped below the element, which
		// is where the rotate control lives.
		const halfWidth = ELEMENT_TOOLBAR_WIDTH / 2;
		const maxLeft = Math.max(halfWidth + 4, hostRect.width - halfWidth - 4);
		const left = Math.min(Math.max(centerX, halfWidth + 4), maxLeft);
		const top = Math.max(topEdge - ELEMENT_TOOLBAR_GAP, ELEMENT_TOOLBAR_HEIGHT + 4);

		setElementToolbar(prev => (
			prev && Math.abs(prev.left - left) < 1 && Math.abs(prev.top - top) < 1
				? prev
				: { left, top }
		));
	}

	useEffect(() => {
		currentSideRef.current = currentSide;
	}, [currentSide]);
	
	useEffect(() => {
		if (cursorInBoundingBox !== false) {
			//console.log("inbox");
			const label = canvasRef.current.getObjects().find(obj => obj.parent === cursorInBoundingBox);
			if(label){
				label.set({ visible: false });
			}
			//console.log(label);
			//console.log(showBoundingBoxAddBtnTimer);
			//if(showBoundingBoxAddBtnTimer != null){
			//	clearTimeout(showBoundingBoxAddBtnTimer);
			//}
		}else{
			//console.log("out of box");
			// The click that opens the parent's menu also takes the cursor out of
			// the box - showBoundingBoxAddLabels keeps the label down for that.
			if (designIsEmpty()) showBoundingBoxAddLabels(true);
		}
	}, [cursorInBoundingBox]);
	
  	useEffect(async() => {
		await new Promise(resolve => {
			fetch('/api/fonts-css').then(res => res.text()).then(css => {
				const style = document.createElement('style');
		        style.textContent = css;
		        document.head.appendChild(style);
				resolve();
		    });
		});
		
		// Must run before any image is created: it sets the resample filter every
		// fabric.Image inherits, so uploads do not preview softer than they are.
		enableHighQualityDownscaling();

		// Before any object exists: it patches the prototype every transform reads.
		fixControlTransforms(fabric);

		// Before any text exists, saved designs included: arced text rebuilds its
		// path from arcAmount when it is measured.
		installTextArc(fabric);

    	const canvas = new fabric.Canvas('canvas', {
      		height: 900,
      		width: 950,
      		//backgroundColor: '#f0f0f0',
      		selection: false, // enables selection box and controls
      		perPixelTargetFind: false // use bounding box for selection
    	});

		canvasRef.current = canvas;
		const stopContextRestore = restoreOnContextLoss(canvas);

		

		// Instantiate the plain JS TextTools helper now that the canvas exists
		try {
			textToolsInstanceRef.current = new TextTools({ canvasRef, fabric, handleSave: handleSaveWrapper, boundingBoxes: boundingBoxes[currentSideRef.current], saveHistory, fitObjectAfterEdit });
			imageToolsInstanceRef.current = new ImageTools({ canvasRef, handleSave: handleSaveWrapper, boundingBoxes: boundingBoxes[currentSideRef.current] });
			downloadToolsInstanceRef.current = new DownloadTools({ sides, SITE_URL });
			contrastToolsInstanceRef.current = new ContrastTools({ canvasRef });
			gridToolsInstanceRef.current = new GridTools();
			fpdLoaderToolsInstanceRef.current = new FPDLoaderTools();
		} catch (e) {
			console.warn('Could not create TextTools instance:', e);
		}
		
		// Save initial state
		//saveHistory();

		// Save history on relevant events
		const saveEvents = ['object:added', 'object:modified', 'object:removed'];
		saveEvents.forEach(event => {
			canvas.on(event, (e) => {
				if(e.target && (e.target.type === 'text' || e.target.type === 'image')) {
					saveHistory();
				}
			});
			canvas.on('object:added', (e) => {
				if (e.target) setCustomControls(e.target);
			});
		});
		
		fabric.loadSVGFromString(`<svg width="37" height="37" viewBox="0 0 37 37" fill="none" xmlns="http://www.w3.org/2000/svg">
			<rect x="0.5" y="0.5" width="36" height="36" rx="18" fill="white"/>
			<rect x="0.5" y="0.5" width="36" height="36" rx="18" stroke="#DEE1EA"/>
			<path d="M18.2656 9.33496V27.1958" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
			<path d="M9.33496 18.2109H27.1958" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
			</svg>`,
			function (objects, options) {
				boundingBoxAddBtn = fabric.util.groupSVGElements(objects, options);
				boundingBoxAddBtnRef.current = boundingBoxAddBtn;
				boundingBoxAddBtn.set({ left: -100, top: -100, scaleX: 1, scaleY: 1, selectable: false, evented: false, visible: false });
				boundingBoxAddBtn.setCoords();
				canvas.add(boundingBoxAddBtn);
			}
		);

    	canvas.on('selection:created', handleSelection);
    	canvas.on('selection:updated', handleSelection);
    	canvas.on('selection:cleared', () => {
			setSelectedObject(null);
			blurTextZoom();
			// The controls go with the selection, so the label has to go too - most
			// visibly after a delete, which clears the selection from under the pointer.
			clearControlHint();
			window.parent.postMessage({ action: 'deselect', value: '' }, '*');
			canvasRef.current.renderAll();
    	});
    	canvas.on('object:scaling', (e) => {
      		const obj = e.target;

			// Nothing on a shirt is ever meant to be mirrored, and this is where
			// that is enforced - not with lockScalingFlip, which refuses the whole
			// transform and kills the resize instead.
			//
			// A drag that runs past its anchor asks fabric for a negative scale, and
			// fabric answers by toggling flipX/flipY and storing the size positive
			// (see Object.prototype._set). So the mirroring never arrives as a
			// negative scaleX for clampScale below to fold away - it arrives as a
			// flag. Put the flags back the way this drag found them: transform
			// .original is the pose fabric snapshotted when the handle was grabbed,
			// so a design that was already mirrored stays as it was rather than
			// being quietly straightened.
			const grabbed = e.transform && e.transform.original;
			if (grabbed) {
				if (obj.flipX !== grabbed.flipX) obj.flipX = grabbed.flipX;
				if (obj.flipY !== grabbed.flipY) obj.flipY = grabbed.flipY;
			}

			// Runs before handleObjectScaling, so this is where a collapsed scale
			// gets floored - everything downstream measures the object and a zero
			// size is a state the user cannot drag back out of.
			obj.scaleX = clampScale(obj.scaleX);
			obj.scaleY = clampScale(obj.scaleY);
      		if (obj.type === 'image') {
        		const width = obj.width * obj.scaleX;
        		const height = obj.height * obj.scaleY;

        		if (width > MAX_WIDTH) {
          			obj.scaleX = MAX_WIDTH / obj.width;
        		}
        		if (height > MAX_HEIGHT) {
          			obj.scaleY = MAX_HEIGHT / obj.height;
        		}
      		}
    	});

    	// Snap to grid handlers
    	function snapToGrid(value, size) {
      		return Math.round(value / size) * size;
    	}

		// Place the label over the control under the pointer. Only the active object
		// has hit-testable controls in fabric, which is exactly the object whose
		// duplicate and delete buttons are on screen.
		function updateControlHint(domEvent) {
			const canvas = canvasRef.current;
			const active = canvas && canvas.getActiveObject();
			const host = editorContentRef.current;
			if (!active || !host || typeof active._findTargetCorner !== 'function') {
				clearControlHint();
				return;
			}

			// Nothing is hovered while something is being dragged, and running this
			// during a transform was expensive: on a small element the pointer that
			// grabbed the scale handle also sits inside the delete halo, so the
			// label latched on and then re-anchored every frame - the icon moves
			// with the element it is being resized from. Each of those frames was a
			// React re-render of this whole component, mid-drag, which is what made
			// resizing stutter.
			//
			// The lookup is also not safe here: _findTargetCorner writes to
			// target.__corner as a side effect, and __corner is the control the
			// running transform was set up from. Fabric skips its own hover
			// handling during a transform for exactly that reason.
			if (canvas._currentTransform) {
				clearControlHint();
				return;
			}

			// The same call fabric makes to pick its own cursor: pointer in canvas
			// coordinates, zoom ignored.
			const corner = active._findTargetCorner(canvas.getPointer(domEvent, true));
			const labels = corner && CONTROL_HINTS[corner];
			const coords = labels && active.oCoords[corner];
			if (!coords) {
				clearControlHint();
				return;
			}

			// oCoords are backing-store pixels, but the canvas is laid out at whatever
			// width fits the viewport, so positions have to be scaled before they mean
			// anything in the page.
			const canvasRect = canvas.upperCanvasEl.getBoundingClientRect();
			const hostRect = host.getBoundingClientRect();
			const scale = canvas.getWidth() ? canvasRect.width / canvas.getWidth() : 1;
			const left = canvasRect.left - hostRect.left + coords.x * scale;
			const top = canvasRect.top - hostRect.top + coords.y * scale;

			// editorContent clips its overflow, so a label above a control near the top
			// would be cut in half. Drop it under the control instead when it will not
			// fit above.
			const below = top < CONTROL_HINT_GAP + 44;
			const label = labels[active.type] || labels.text;

			setControlHint(prev => (
				prev && prev.label === label && prev.below === below &&
				Math.abs(prev.left - left) < 1 && Math.abs(prev.top - top) < 1
					? prev
					: { label, left, top, below }
			));
		}

		// The one rule all three gestures follow: a move, a resize and a rotation
		// are never refused while they are happening. The element is only flagged -
		// its border turns red while any part of it sits outside the region, and
		// goes back to normal once it is fully inside. Putting an element that was
		// let go outside back in is the end handler's job.
		function flagObjectCoverage(obj) {
			const canvas = canvasRef.current;
			const inside = isObjectInsideRegion(obj);

			// Remember the last legal pose so an illegal drop can return there
			// instead of being yanked to some box centre.
			if (inside) obj.__lastValidPose = capturePose(obj);

			const desiredBorder = inside ? (obj.__defaultBorderColor || selectionBorderColorRef.current) : 'red';
			if (obj.borderColor !== desiredBorder) {
				obj.borderColor = desiredBorder;
				if (canvas && !canvas.__drawScheduled) canvas.requestRenderAll();
			}
			return inside;
		}

		// The border a gesture leaves behind, once it is over.
		function restoreObjectBorder(obj) {
			obj.borderColor = obj.__defaultBorderColor || selectionBorderColorRef.current;
		}

		// Tell the parent's font size input the size the text actually ended up
		// at, but only when putting it back inside changed that size - and only
		// once, after it has settled. Getting an element back into the region can
		// take more than one bake, and the sizes in between are numbers the
		// customer should never see appear in the input.
		function notifyTextResized(obj, fontSizeBefore) {
			if (!obj || obj.type !== 'text' || obj.fontSize === fontSizeBefore) return;
			const tools = textToolsInstanceRef.current;
			if (tools && typeof tools.notifyFontSizeChanged === 'function') tools.notifyFontSizeChanged(obj);
		}

    	function handleObjectMoving(e) {
			const obj = e.target;
			if (!obj) return;
			// Magnetic centring runs before the coverage flag, so what is judged is
			// the pose the element actually ends up in this frame.
			applyCenterSnap(obj);
			flagObjectCoverage(obj);
		}

    	function handleObjectScaling(e) {
			const obj = e.target;

			// Snapping a sub-cell object rounds its size down to 0, so keep the
			// unsnapped scale in that case rather than collapsing the object.
			if (gridEnabledRef.current) {
				const snappedW = snapToGrid(obj.scaleX * obj.width, gridSizeRef.current) / obj.width;
				const snappedH = snapToGrid(obj.scaleY * obj.height, gridSizeRef.current) / obj.height;
				obj.set({
					scaleX: snappedW > 0 ? snappedW : clampScale(obj.scaleX),
					scaleY: snappedH > 0 ? snappedH : clampScale(obj.scaleY)
				});
			}

			// After the grid, which can round the size back down, and before the
			// haloes and the coverage flag, which both measure the final size.
			const cornerSize = controlCornerSize();
			keepControlsApart(obj, e.transform, cornerSize);

			// The haloes around the corner icons are measured off the element, so
			// they have to shrink and grow with it.
			refreshControlHitAreas(obj, cornerSize);

			// The size is no longer capped at the edge of the print area. Resizing
			// works the way dragging does: it runs as far as the customer takes it
			// and is only flagged, and object:scaled brings an element that was let
			// go outside back in. Images are still bounded by the MAX_WIDTH /
			// MAX_HEIGHT clamp in the object:scaling handler above. The one limit
			// is on the way down: keepControlsApart, on mobile.
			flagObjectCoverage(obj);
		}

    	function handleObjectRotating(e) {
      		const obj = e.target;
			if (!obj) return;

			// Fabric rotates about the origin, which for these objects is the
			// top-left corner, so the centre is put back afterwards and the element
			// turns on the spot.
      		const center = obj.getCenterPoint();

			// The right-angle snap wins over the grid: every multiple of 90 is on the
			// 15 degree grid anyway, and applying it last keeps the magnetic zone the
			// same width whether the grid is on or off.
			const nextAngle = snapToRightAngle(gridEnabledRef.current ? snapToGrid(obj.angle, 15) : obj.angle);
			obj.set({ angle: nextAngle });
			obj.setPositionByOrigin(center, 'center', 'center');

			// The angle used to be pushed back the moment the element poked out of
			// the print area, which made a rotation near the edge feel like it was
			// fighting the cursor. It turns freely now and is only flagged, the same
			// as a drag; object:rotated puts it back inside afterwards.
			flagObjectCoverage(obj);

			// Not renderAll: that draws the whole canvas now, and fabric still
			// draws it again on the next frame.
      		canvasRef.current.requestRenderAll();
  		}
		
		
		/*canvas.on('touch:gesture', function(e) {
			if (!e.e) return;
			const event = e.e;
			// Only handle two-finger gestures
			if (event.touches && event.touches.length === 2) {
				const MARGIN = 0.5; // threshold for zoom vs pan
				const getMidpoint = (touches) => ({
					x: (touches[0].clientX + touches[1].clientX) / 2,
					y: (touches[0].clientY + touches[1].clientY) / 2
				});
				if (e.self && e.self.scale !== undefined) {
					if (e.self.state === 'start') {
						canvas.selection = false;
						const midpoint = getMidpoint(event.touches);
						pinchState.current = {
							initialZoom: canvas.getZoom(),
							initialScale: e.self.scale,
							lastMidpoint: midpoint
						};
					} else if (e.self.state === 'change' && pinchState.current.initialScale) {
						const scaleDiff = Math.abs(e.self.scale - pinchState.current.initialScale);
						const midpoint = getMidpoint(event.touches);
						if (scaleDiff > MARGIN) {
							let zoom = pinchState.current.initialZoom * (1 + (e.self.scale - pinchState.current.initialScale) * 0.5);
							zoom = Math.max(0.2, Math.min(zoom, 5));
							const rect = canvas.upperCanvasEl.getBoundingClientRect();
							const centerX = rect.width / 2;
							const centerY = rect.height / 2;
							//canvas.setZoom(zoom);
							canvas.zoomToPoint({ x: centerX, y: centerY }, zoom);
						} else {
							// Pan by difference in midpoint
							const last = pinchState.current.lastMidpoint;
							const dx = midpoint.x - last.x;
							const dy = midpoint.y - last.y;
							let vpt = canvas.viewportTransform;
							vpt[4] += dx * 1.5;
							vpt[5] += dy * 1.5;
							canvas.setViewportTransform(vpt);
							pinchState.current.lastMidpoint = midpoint;
						}
					}
				}
			}
		});*/
		
		window.addEventListener('touchend', (e) => {
			// No drag selection box on mobile: a finger drag on empty canvas is
			// far more often a mistouch or a scroll than a multi-select.
			canvas.selection = window.innerWidth >= 600;
			pinchState.current = {};
		});
				
		canvas.on('object:moving', handleObjectMoving);
    	canvas.on('object:scaling', handleObjectScaling);
    	canvas.on('object:rotating', handleObjectRotating);
		
		canvas.on('object:moved', (e) => {
			//console.log(e.target);
			const obj = e.target;
			obj.lockMovementX = false;
			obj.lockMovementY = false;

			// Before the save at the end of this handler, which captures the
			// canvas for the thumbnail: a guide still up would be photographed
			// along with the design.
			hideSnapLines();

			// restore the normal border now that the drag has ended
			restoreObjectBorder(obj);

			// An element that legally spans several boxes is left exactly where
			// the user dropped it. Only step in when it is genuinely outside the
			// region, and then prefer the last pose it was legal at.
			//
			// A drag changes no sizes, so there is nothing to bake - but this goes
			// through the same enforcement as the other two gestures so there is
			// one answer to "is it inside when the action is over", not three.
			const region = getRegion();
			if (!isObjectInsideRegion(obj)) {
				enforceObjectInRegion(region, obj, { lastValidPose: obj.__lastValidPose, bakeText: false });
				if (canvas && !canvas.__drawScheduled) canvas.requestRenderAll();
			}
			if (isObjectInsideRegion(obj)) obj.__lastValidPose = capturePose(obj);

			// Which box receives the next inserted element (no longer a constraint).
			const c = objectCenter(obj);
			const containing = insertBoxAt(c.x, c.y);
			if (containing) setActiveBoundingBox(containing);

			if (gridEnabledRef.current) {
				const bounds = polygonBounds(objectPolygon(obj));
				const w = bounds.x1 - bounds.x0;
				const h = bounds.y1 - bounds.y0;
				const beforeSnap = capturePose(obj);
				const step = gridSizeRef.current;

				// Snap to the grid, then keep the result only if it is still
				// covered; otherwise try the neighbouring cells before giving up
				// and leaving the element where it was.
				const baseLeft = snapToGrid(bounds.x0, step);
				const baseTop = snapToGrid(bounds.y0, step);
				const offsets = [[0, 0], [1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]];

				let placed = false;
				for (const [dx, dy] of offsets) {
					obj.setPositionByOrigin({
						x: baseLeft + dx * step + w / 2,
						y: baseTop + dy * step + h / 2
					}, 'center', 'center');
					obj.setCoords();
					if (region.isEmpty || isObjectInsideRegion(obj)) { placed = true; break; }
				}
				if (!placed) applyPose(obj, beforeSnap);
				if (isObjectInsideRegion(obj)) obj.__lastValidPose = capturePose(obj);
				if (canvas && !canvas.__drawScheduled) canvas.requestRenderAll();
			}
			console.log("moved");
			handleSave(selectedShirtColorRef.current);	
		});
		canvas.on('object:scaled', (e) => {
			const obj = e.target;
			obj.lockScalingX = false;
			obj.lockScalingY = false;
			restoreObjectBorder(obj);

			const region = getRegion();

			// The same put-it-back a drop gets, with one difference: no last valid
			// pose is offered. That pose still carries the size from before this
			// drag, so restoring it would undo the resize rather than place it.
			// Without one the snap moves the element - new size intact - and only
			// shrinks it when there is no position it fits at.
			//
			// The snap and the bake that follows it take turns until the element
			// is inside, rather than one each: baking the drag's scale into
			// fontSize can make the text slightly bigger than the size that was
			// just snapped in, and at the floor font size it can make it several
			// times bigger, which is how a text scaled down small against an edge
			// used to be left hanging outside the print area.
			const fontSizeBefore = obj.fontSize;
			const covered = enforceObjectInRegion(region, obj);
			notifyTextResized(obj, fontSizeBefore);

			// The scale is no longer capped during the drag, so this is the first
			// point at which there is an answer to give: locked only when even the
			// snap could not get the element inside, which for text means it is
			// sitting at the floor font size.
			// The snap above may have shrunk the element to fit, so the haloes are
			// sized off the element one last time now that it has stopped moving.
			refreshControlHitAreas(obj, controlCornerSize());

			const locked = !covered;
			obj.clamped = locked;
			window.parent.postMessage({ action: 'scalingLocked', value: locked }, '*');

			if (!locked) obj.__lastValidPose = capturePose(obj);
			if (canvas && !canvas.__drawScheduled) canvas.requestRenderAll();

			handleSave(selectedShirtColorRef.current);
		});
		canvas.on('object:rotated', (e) => {
			const obj = e.target;
			obj.lockRotation = false;
			restoreObjectBorder(obj);

			// No last valid pose here either, and for the same reason: it carries
			// the angle from before this rotation. Without one the element keeps
			// the angle it was let go at and is moved back inside at that angle.
			// A scale the snap leaves behind is baked into fontSize - text carries
			// its size there alone - and the bake is followed back into the region
			// if it overshot, the same as after a resize.
			const region = getRegion();
			const fontSizeBefore = obj.fontSize;
			enforceObjectInRegion(region, obj);
			// Nothing to report unless the recovery had to shrink the text: a
			// rotation that fits leaves the size exactly as it was.
			notifyTextResized(obj, fontSizeBefore);

			if (isObjectInsideRegion(obj)) obj.__lastValidPose = capturePose(obj);
			if (canvas && !canvas.__drawScheduled) canvas.requestRenderAll();

			handleSave(selectedShirtColorRef.current);
		});
		
		canvas.on('mouse:move', function(e) {
			//console.log(e.target);

			updateControlHint(e.e);

			const pointer = canvas.getPointer(e.e);
			// The "+" stands in for a mouse pointer. A finger has none, but fabric
			// reports a touch drag as mouse:move too, so a drag that started outside
			// a print area and slid into one drew it under the finger.
			const touch = window.innerWidth < 600 ||
				(e.e && (e.e.pointerType === 'touch' || (e.e.type && e.e.type.startsWith('touch'))));
			if (e.target && e.target.type === 'boundingBox' && !touch) {
				// A move that lands on the position the add cursor was taken down at
				// is not the customer moving the mouse - the pointer is still on the
				// button they pressed. Leave the "+" down, and give the pointer its
				// ordinary arrow back: hiding the system cursor is only there because
				// the "+" stands in for it.
				if (addCursorHeldAt(e.e)) {
					e.target.set({ hoverCursor: 'default' });
					return;
				}
				// While the parent's menu is open the customer is already busy with
				// an action; the "+" would only invite them to start a second one.
				if (menuOpenRef.current) {
					e.target.set({ hoverCursor: 'default' });
					return;
				}

				// Hover is reported per region, so entering any box of a region
				// hides that region's single add button.
				setCursorInBoundingBox(e.target.regionId != null ? e.target.regionId : e.target.id);
				e.target.set({ hoverCursor: 'none' });

				boundingBoxAddBtn.set({ left: pointer.x - 16, top: pointer.y - 16, visible: true });
				boundingBoxAddBtn.setCoords();
				boundingBoxAddBtn.bringToFront();
				canvas.renderAll();
			}else{
				if (boundingBoxAddBtn) {
					setCursorInBoundingBox(false);
					// Only when the "+" actually goes. This branch runs on every
					// move of a drag, and a full synchronous render here came on
					// top of the one fabric already schedules for that frame.
					if (boundingBoxAddBtn.visible) {
						boundingBoxAddBtn.set({ visible: false });
						canvas.requestRenderAll();
					}
				}
			}

			/*if(canvas.getActiveObject()){
				if(canvas.getActiveObject().lockMovementX && pointer.x > canvas.getActiveObject().getBoundingRect().left + canvas.getActiveObject().getBoundingRect().width){
					canvas.getActiveObject().lockMovementX = false;
				}
			}*/
		});

		canvas.on('mouse:down', function(e) {
			// A press anywhere else on the canvas is the customer moving on from
			// the menu they had open.
			if (!e.target || e.target.type !== 'boundingBox') menuClosed();
			if (e.target && e.target.type === 'boundingBox') {
				const pointer = canvas.getPointer(e.e);
				setActiveBoundingBox(e.target.regionBox || e.target);
				const canvasDOM = canvas.upperCanvasEl;
				const rect = canvasDOM.getBoundingClientRect();
				const left = rect.left;
				const top = rect.top;
				window.parent.postMessage({ action: 'openMenu', value: { clientX: pointer.x, clientY: pointer.y, left: left, top: top } }, '*');
				// The menu the parent is opening takes over from the add cursor and
				// the "Design toevoegen" label: the customer has asked for it, so
				// neither has anything left to say and both would sit there under
				// the menu. They stay away for as long as it is open.
				menuOpened(e.e);
			}
			if (e.target && e.target.type !== 'boundingBox') {
				try {
					const obj = e.target;
					// remember the normal border colour so we can restore it after a drag
					if (obj.borderColor !== 'red') obj.__defaultBorderColor = obj.borderColor;
					// Seed the recovery pose so a drag that starts legal can always
					// fall back to where it began.
					if (isObjectInsideRegion(obj)) obj.__lastValidPose = capturePose(obj);
					const c = objectCenter(obj);
					const box = insertBoxAt(c.x, c.y);
					if (box) setActiveBoundingBox(box);
				} catch (err) {}
			}

		});				

		const loadColors = (onRetry) => fetchJson(`/api/colors?productId=${productId}`, { onRetry })
			.then(data => {
				setColors(data);
				if (data && data.length > 0) {
					// A selectColor from the parent can easily beat this fetch -
					// it does not even start until /api/fonts-css has answered -
					// so apply the parked one now that the list can resolve it,
					// and only fall back to the first colour when nothing else
					// has claimed the choice. Seeding unconditionally is what
					// overwrote a colour the parent had already selected.
					const pending = pendingColorRef.current ? findColor(data, pendingColorRef.current) : null;
					if (pending) {
						pendingColorRef.current = null;
						applyShirtColor(pending.color, 'parent');
					} else {
						if (pendingColorRef.current) {
							console.warn('selectColor: unknown colour for this product', pendingColorRef.current);
							pendingColorRef.current = null;
						}
						applyShirtColor(data[0].color, 'default');
					}
					let tmp_sides = [];
					if(data[0]['front'] != null){
						tmp_sides.push('front');
					}
					if(data[0]['back'] != null){
						tmp_sides.push('back');
					}
					if(data[0]['left'] != null){
						tmp_sides.push('left');
					}
					if(data[0]['right'] != null){
						tmp_sides.push('right');
					}
					setSides(tmp_sides);
				}
			}
		);

		const loadBoundingBoxes = (onRetry) => fetchJson(`/api/bounding-boxes?productId=${productId}`, { onRetry })
			.then(data => {
				//setColors(data);
				//console.log(data);
				if (data) {
					//setSelectedShirtColor(data[0]);
					//let tmp_sides = [];
					if(data['voorkant'] != null){
						data['voorkant'].forEach((box, index) => {
							box.id = `front-${index}`;
							box.left = box.left - box.width / 2;
							box.top = box.top - box.height / 2;
						});
						setBoundingBoxes(prev => ({ ...prev, front: data['voorkant'] }));
					}
					if(data['achterkant'] != null){
						data['achterkant'].forEach((box, index) => {
							box.id = `back-${index}`;
							box.left = box.left - box.width / 2;
							box.top = box.top - box.height / 2;
						});
						setBoundingBoxes(prev => ({ ...prev, back: data['achterkant'] }));
					}
					if(data['links'] != null){
						data['links'].forEach((box, index) => {
							box.id = `left-${index}`;
							box.left = box.left - box.width / 2;
							box.top = box.top - box.height / 2;
						});
						setBoundingBoxes(prev => ({ ...prev, left: data['links'] }));
					}
					if(data['rechts'] != null){
						data['rechts'].forEach((box, index) => {
							box.id = `right-${index}`;
							box.left = box.left - box.width / 2;
							box.top = box.top - box.height / 2;
						});
						setBoundingBoxes(prev => ({ ...prev, right: data['rechts'] }));
					}
					//setSides(tmp_sides);
					console.log(boundingBoxesRef.current);
				}
			}
		);

		// The editor is not usable without these two: no colours means no shirt to
		// design on, and no bounding boxes means nothing keeps artwork inside the
		// print area. Each one is retried by fetchJson; only when that gives up is
		// the customer stopped and asked to retry.
		const essentialLoads = { colors: loadColors, boundingBoxes: loadBoundingBoxes };
		const loadState = { colors: 'loading', boundingBoxes: 'loading' }; // loading | retrying | done | failed
		const showLoadState = () => {
			const states = Object.values(loadState);
			const busy = states.some(s => s === 'loading' || s === 'retrying');
			if (!busy && states.includes('failed')) setConnectionIssue('failed');
			else if (states.includes('retrying') || states.includes('failed')) setConnectionIssue('slow');
			else setConnectionIssue(null);
		};
		const runEssentialLoad = (name) => {
			loadState[name] = 'loading';
			showLoadState();
			essentialLoads[name](() => { loadState[name] = 'retrying'; showLoadState(); })
				.then(
					() => { loadState[name] = 'done'; },
					err => { console.error(`Loading ${name} failed:`, err); loadState[name] = 'failed'; }
				)
				.finally(showLoadState);
		};
		Object.keys(essentialLoads).forEach(runEssentialLoad);
		retryEssentialLoadsRef.current = () => {
			Object.keys(loadState).filter(name => loadState[name] === 'failed').forEach(runEssentialLoad);
		};

		// mouse:move stops firing the moment the pointer leaves the canvas, so a label
		// shown on a control at the edge would stay behind. A DOM mouseleave is the
		// unambiguous signal here - fabric's mouse:out also fires when moving from the
		// element onto its own control, which is precisely when the label should show.
		const hideControlHint = () => clearControlHint();
		canvas.upperCanvasEl.addEventListener('mouseleave', hideControlHint);

		// One handler for every way the element can move: selecting it, dropping it,
		// undoing, switching sides, an edit driven from the parent. The comparison
		// inside is what keeps a render-frequency event from re-rendering React.
		canvas.on('after:render', updateElementToolbar);
		// Not object:moved: a drag released off the canvas does not always report
		// one, and a guide left standing would end up in the downloaded design.
		// mouse:up comes after the end events, so the put-back they do is over by
		// the time the guides go.
		const clearSnapGuides = () => hideSnapLines();
		canvas.on('mouse:up', clearSnapGuides);

		const suspendElementToolbar = () => { elementToolbarBusyRef.current = true; };
		const resumeElementToolbar = () => {
			elementToolbarBusyRef.current = false;
			updateElementToolbar();
		};
		const transformStart = ['object:moving', 'object:scaling', 'object:rotating'];
		// mouse:up as well as the three end events: a transform that is released
		// outside the canvas does not always come back as object:moved.
		const transformEnd = ['object:moved', 'object:scaled', 'object:rotated', 'mouse:up'];
		transformStart.forEach(event => canvas.on(event, suspendElementToolbar));
		transformEnd.forEach(event => canvas.on(event, resumeElementToolbar));

    	const cleanup = () => {
      		canvas.off('object:moving', handleObjectMoving);
      		canvas.off('object:scaling', handleObjectScaling);
      		canvas.off('object:rotated', handleObjectRotating);
			canvas.off('after:render', updateElementToolbar);
			canvas.off('mouse:up', clearSnapGuides);
			transformStart.forEach(event => canvas.off(event, suspendElementToolbar));
			transformEnd.forEach(event => canvas.off(event, resumeElementToolbar));
			canvas.upperCanvasEl.removeEventListener('mouseleave', hideControlHint);
			saveEvents.forEach(event => {
				canvas.off(event, saveHistory);
			});
    	};

    	return () => {
      		cleanup();
			stopContextRestore();
      		canvas.dispose();
    	};
  	}, []);

	// A dropped connection that comes back is the moment to try again - the
	// customer should not have to notice that it did.
	useEffect(() => {
		const goOnline = () => {
			setIsOffline(false);
			if (retryEssentialLoadsRef.current) retryEssentialLoadsRef.current();
		};
		const goOffline = () => setIsOffline(true);
		window.addEventListener('online', goOnline);
		window.addEventListener('offline', goOffline);
		return () => {
			window.removeEventListener('online', goOnline);
			window.removeEventListener('offline', goOffline);
		};
	}, []);

	useEffect(() => {
		//console.log(loadingFPDData);
		if (loadingFPDData){
			setLoadingFPDData(false);
			return;
		} 
		if (!colors.length) return;
		loadDesign(productId, sessionId);
	}, [colors]);

	// The parent anchors its own overlays to the canvas, so every move of the
	// canvas on screen has to be pushed back out - a window resize moves it
	// without anything on this side firing.
	function postCanvasPos(){
		const canvas = canvasRef.current;
		if (!canvas) return;
		const rect = canvas.upperCanvasEl.getBoundingClientRect();
		window.parent.postMessage({ action: 'canvasPos', value: { left: rect.left, top: rect.top } }, '*');
	}

	// Zooms in on the text being edited, centred in the band of the iframe the
	// keyboard leaves free. The parent sends that band with focusText, in iframe
	// pixels, because only the top-level page can see the keyboard; without it the
	// whole iframe is assumed visible. Called again after every keystroke, so text
	// that grows wider zooms back out far enough to stay on screen.
	function focusTextZoom(band) {
		if (window.innerWidth >= 600) return;
		const canvas = canvasRef.current;
		const container = document.querySelector('div.canvas-container');
		const obj = canvas && canvas.getActiveObject();
		if (!container || !obj || obj.type !== 'text') {
			blurTextZoom();
			return;
		}

		if (!textFocusRef.current) {
			textFocusRef.current = { baseTransform: container.style.transform, obj };
			container.style.transition = 'transform 150ms ease-out';
		}
		const focus = textFocusRef.current;
		if (band) focus.band = band;
		const top = focus.band && Number.isFinite(focus.band.top) ? focus.band.top : 0;
		const bottom = focus.band && Number.isFinite(focus.band.bottom) ? focus.band.bottom : window.innerHeight;
		const bandHeight = Math.max(bottom - top, 1);

		// The container's layout box, without any zoom on it. Not measured by
		// switching the transform off for a moment: with the transition on, that
		// starts an animation and getBoundingClientRect answers with the old, zoomed
		// box - which pushed the text far off to the right and down. Offsets ignore
		// transforms, so the box is built from those and the untransformed parent.
		const parent = container.offsetParent || document.body;
		const parentRect = parent.getBoundingClientRect();
		const layout = {
			left: parentRect.left + parent.clientLeft - parent.scrollLeft + container.offsetLeft,
			top: parentRect.top + parent.clientTop - parent.scrollTop + container.offsetTop,
			width: container.offsetWidth,
			height: container.offsetHeight
		};

		// Canvas pixels to container pixels. getBoundingRect includes the
		// canvas's own pinch zoom.
		const k = container.offsetWidth / canvas.getWidth();
		const box = obj.getBoundingRect();
		const w = Math.max(box.width * k, 1);
		const h = Math.max(box.height * k, 1);
		const cx = (box.left + box.width / 2) * k;
		const cy = (box.top + box.height / 2) * k;

		// Not zoomed out past the shirt view: that only makes the text smaller.
		const baseMatch = /scale\(([\d.]+)\)/.exec(focus.baseTransform || '');
		const baseScale = baseMatch ? parseFloat(baseMatch[1]) : 1;
		const preferred = Math.max(baseScale, Math.min(
			TEXT_FOCUS_MAX_WIDTH * window.innerWidth / w,
			TEXT_FOCUS_MAX_HEIGHT * bandHeight / h
		));
		// Unless the whole text would not fit even at the shirt view - text turned
		// upright on a side view is far taller than the band above the keyboard.
		// Then it zooms out as far as it takes to keep all of it in sight.
		const fits = Math.min(
			TEXT_FOCUS_MAX_WIDTH * window.innerWidth / w,
			Math.max(bandHeight - 2 * MOBILE_SHIRT_PADDING_Y, 1) / h
		);
		const scale = Math.min(preferred, fits);

		// Same transform shape as the shirt crop, about the container's top centre:
		// a point p lands at origin + translate + scale * (p - origin).
		const x = window.innerWidth / 2 - (layout.left + layout.width / 2) - scale * (cx - layout.width / 2);
		const y = top + bandHeight / 2 - layout.top - scale * cy;
		container.style.transformOrigin = 'top center';
		container.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
		// The controls are sized in canvas pixels for the zoom they were made at, so
		// they would be blown up along with the text. Remade for the new zoom, they
		// keep their usual size on screen.
		setCustomControls(obj, layout.width * scale);
		obj.setCoords();
		canvas.requestRenderAll();
		postCanvasPos();
	}

	// Back to the shirt view once typing is over.
	function blurTextZoom() {
		const focus = textFocusRef.current;
		if (!focus) return;
		textFocusRef.current = null;
		const canvas = canvasRef.current;
		const container = document.querySelector('div.canvas-container');
		if (container) {
			container.style.transform = focus.baseTransform;
			// And back to the size they have in the shirt view.
			const baseMatch = /scale\(([\d.]+)\)/.exec(focus.baseTransform || '');
			setCustomControls(focus.obj, container.offsetWidth * (baseMatch ? parseFloat(baseMatch[1]) : 1));
			focus.obj.setCoords();
			if (canvas) canvas.requestRenderAll();
			const done = () => {
				// Typing started again before the zoom out had finished.
				if (textFocusRef.current) return;
				container.style.transition = '';
				postCanvasPos();
			};
			container.addEventListener('transitionend', done, { once: true });
			// transitionend never fires when the transform did not actually change.
			setTimeout(done, 200);
		}
	}

	function loadDesign(pid, sid){
		const canvas = canvasRef.current;
		postCanvasPos();
		fetch(`/api/load-design?productId=${pid}&a=${sid}`)
		.then(res => res.json())
		.then(data => {
			// Offered, not imposed: a selectColor that arrived while this fetch
			// was in flight is the live choice and outranks what was stored.
			applyShirtColor(data.selectedShirtColor, 'stored');

			let sides = [];
			if(data.sides){
				sides = JSON.parse(data.sides);
			}

			// Ref and state together, so the thumbnail run below merges into the
			// stored design and not into whatever the ref held before it.
			designsRef.current = sides;
			setDesigns(sides);
			// Whatever won above - never data.selectedShirtColor directly.
			const colorObj = findColor(colors, selectedShirtColorRef.current);

			// The side that is on screen now, not the one that was when the effect
			// above called us. The parent puts the side buttons up before this
			// fetch answers, so the customer can and does switch while it is in
			// flight - and `currentSide` is a closure variable, frozen at the
			// render that registered the call. Using it drew the first side's
			// design onto the side they had just switched to, and the save at the
			// end of loadSide - which reads the ref, and so had caught up - then
			// stored that design, thumbnail and all, under the switched-to side.
			//
			// Redrawing is not optional either: a switch made before the design
			// arrived loaded that side out of an empty designsRef, so the stored
			// design for it is only now available to draw.
			const side = currentSideRef.current;

			if (colorObj) {
				// The stored thumbnails were drawn on the stored colour. When a
				// parent's choice outranked it, they show the wrong shirt - and a
				// selectColor that arrived during the fetch has already redrawn
				// them, only for `sides` above to put the stored ones back.
				if (data.selectedShirtColor !== colorObj.color) {
					makeOtherSidesThumbnails(side, colorObj, sides);
				}
				loadSide(canvas, sides[side] || [], side, productId, colorObj);
			}else if (colors.length > 0) {
				// The colour is not one this product offers any more. Degrade to
				// the first one regardless of source, or loadSide has no image
				// to draw.
				setShirtColor(colors[0].color);
				loadSide(canvas, sides[side] || [], side, productId, colors[0]);
			}
			// No colour list yet - a loadDesign from the parent can beat
			// /api/colors. The effect on [colors] runs this again once it lands.
		});
	}

	useEffect(() => {
		// Coalesced to one post per frame: a drag-resize fires the event
		// continuously and each one costs a layout read plus a postMessage.
		let frame = null;
		const handleResize = () => {
			if (frame !== null) return;
			frame = requestAnimationFrame(() => {
				frame = null;
				postCanvasPos();
				// The canvas is laid out to fit the viewport, so a resize moves the
				// element under the toolbar without the canvas re-rendering.
				updateElementToolbar();
			});
		};
		window.addEventListener('resize', handleResize);
		return () => {
			if (frame !== null) cancelAnimationFrame(frame);
			window.removeEventListener('resize', handleResize);
		};
	}, []);

  	useEffect(() => {
    	const canvas = canvasRef.current;
    	if (!canvas) return;
    	// Remove old grid lines
    	canvas.getObjects('line').forEach(line => {
      		if (line.gridLine) canvas.remove(line);
    	});
    	if (!gridEnabledRef.current) {
      		canvas.renderAll();
      		return;
    	}
    	for (let i = gridSizeRef.current; i < canvas.width; i += gridSizeRef.current) {
      		const vLine = new fabric.Line([i, 0, i, canvas.height], {
        		stroke: '#ccc', selectable: false, evented: false, gridLine: true
      	    });
      		canvas.add(vLine);
      		vLine.sendToBack();
    	}
    	for (let i = gridSizeRef.current; i < canvas.height; i += gridSizeRef.current) {
      		const hLine = new fabric.Line([0, i, canvas.width, i], {
        		stroke: '#ccc', selectable: false, evented: false, gridLine: true
      		});
      		canvas.add(hLine);
      		hLine.sendToBack();
    	}
    	canvas.renderAll();
  	}, [gridEnabled, gridSize]);
	
	// Listen for messages from parent to change color (and other simple commands)
	useEffect(() => {
		function onMessage(e) {
			const msg = e.data;
			if (!msg || typeof msg !== 'object') return;
			//console.log(msg);
			if (msg.action === 'selectColor' && msg.value) {
				// find color object by name and apply
				const colorObj = findColor(colors, msg.value);
				if (!colorObj || !canvasRef.current) {
					// The colour list has not arrived yet (or the canvas has
					// not). Park the choice; the colours fetch applies it as
					// soon as it can resolve the name. Dropping it here left
					// the default colour on screen - and the save at the end of
					// the load then wrote that default over the real one.
					pendingColorRef.current = msg.value;
					return;
				}
				applyShirtColor(colorObj.color, 'parent');
				// The ref, not the closure: this listener is re-registered on a
				// render, so between a side switch and that render `currentSide`
				// still names the side the customer has left.
				const side = currentSideRef.current;
				makeOtherSidesThumbnails(side, colorObj);
				loadSide(canvasRef.current, designsRef.current[side] || [], side, productId, colorObj);
			}
			if(msg.action === 'undo'){
				handleUndo();
			}
			if(msg.action === 'redo'){
				handleRedo();
			}
			// The menu the customer opened from a print area is over: either the
			// parent says so outright, or it is carrying out the action the menu
			// offered - and then the element it adds decides what the canvas shows.
			if(msg.action === 'closeMenu'){
				menuClosed();
			}
			if(msg.action === 'addText' || msg.action === 'addImage' || msg.action === 'editText' || msg.action === 'editImage'){
				menuClosed(false);
			}
			if(msg.action === 'addText'){
				console.log(designs);
				if (textToolsInstanceRef.current && typeof textToolsInstanceRef.current.addText === 'function') {
					textToolsInstanceRef.current.addText(msg.value, activeBoundingBoxRef.current);
				} else {
					console.warn('TextTools instance not available to addText');
				}
			}
			if(msg.action === 'editText'){
				if (textToolsInstanceRef.current && typeof textToolsInstanceRef.current.editText === 'function') {
					// Re-centred once the edit has landed: a font has to load first.
					Promise.resolve(textToolsInstanceRef.current.editText(msg.value))
						.then(() => { if (textFocusRef.current) focusTextZoom(); });
				} else {
					console.warn('TextTools instance not available to editText');
				}
			}
			if(msg.action === 'addImage'){
				if (imageToolsInstanceRef.current && typeof imageToolsInstanceRef.current.addImage === 'function') {
					imageToolsInstanceRef.current.addImage(msg.value, activeBoundingBoxRef.current);
				} else {
					console.warn('ImageTools instance not available to addImage');
				}
			}
			if(msg.action === 'editImage'){
				if (imageToolsInstanceRef.current && typeof imageToolsInstanceRef.current.editImage === 'function') {
					imageToolsInstanceRef.current.editImage(msg.value);
				} else {
					console.warn('ImageTools instance not available to addImage');
				}
			}
			// The parent's text input gained or lost focus - see focusTextZoom.
			if(msg.action === 'focusText'){
				focusTextZoom(msg.value || null);
			}
			if(msg.action === 'blurText'){
				blurTextZoom();
			}
			if(msg.action === 'deselect'){
				blurTextZoom();
			    if (canvasRef.current) {
					canvasRef.current.discardActiveObject();
					canvasRef.current.requestRenderAll();
				}
			}
			if(msg.action === 'downloadDesign'){
				if (downloadToolsInstanceRef.current && typeof downloadToolsInstanceRef.current.downloadDesign === 'function') {
					// The instance is built at mount, before the product's own sides are
					// known: without this an allSides download renders the four defaults,
					// including sides this product does not print.
					downloadToolsInstanceRef.current.sides = sides;
					// The print method is not something the editor knows. Fixed for
					// now; a `bedrukking` sent along by the shop page takes precedence.
					downloadToolsInstanceRef.current.downloadDesign(msg.value.withProduct, msg.value.format, msg.value.allSides, designsRef, selectedShirtColorRef, colors, currentSideRef.current, { sku: productId, printMethod: msg.value.bedrukking || DEFAULT_PRINT_METHOD });
				} else {
					console.warn('DownloadTools instance not available to downloadDesign');
				}
			}
			if(msg.action === 'loadDesign'){
				// Another product brings its own stored colour, and a colour
				// picked for the previous one must not outrank it.
				if (msg.value.productId !== productId) colorSourceRef.current = 'default';
				setProductId(msg.value.productId);
				setSessionId(msg.value.sessionId);
				//clear session cookie
				//console.log(document.cookie);
				loadDesign(msg.value.productId, msg.value.sessionId);
			}
			if(msg.action === 'grid'){
				applyGrid(msg.value.enabled === true, msg.value.size);
			}
			if(msg.action === 'loadFPDData'){
				// The order's colour is authoritative, and it reaches loadSide
				// (and its save) inside load_order - so it has to move the ref,
				// not just the state.
				const applyOrderColor = (color) => applyShirtColor(color, 'parent');
				fpdLoaderToolsInstanceRef.current.load_order(setProductId, setColors, applyOrderColor, setDesigns, canvasRef.current, loadSide, setLoadingFPDData, msg.value);
			}
			if(msg.action === 'loadSide'){
				setCurrentSide(sides[msg.value] || 'front');
				handleSideChange(sides[msg.value] || 'front');
			}
		}
		if(window.parent !== window){
			window.addEventListener('message', onMessage);
		}
		return () => {
			if(window.parent !== window){
				window.removeEventListener('message', onMessage);
			}
		};
	}, [colors, designs, currentSide, productId]);

	// Keyboard shortcuts for undo/redo, driving the same two handlers the
	// parent's toolbar reaches over postMessage - so the parent's button state
	// stays in sync without anything extra, both handlers post it themselves.
	//
	// They have to be bound in here as well as in the page around it: while the
	// focus is inside this frame the keystroke never reaches that page, so without
	// this the chord does nothing the moment the customer has touched the canvas.
	useEffect(() => {
		function onKeyDown(e) {
			// metaKey so the shortcut also works on a Mac, where ctrl+z is not
			// what anyone presses. altKey is a different chord entirely.
			if (!(e.ctrlKey || e.metaKey) || e.altKey) return;

			// Never take the shortcut away from a field being typed in: there the
			// browser's undo of that text is what the user is asking for.
			const el = e.target;
			if (el && (el.isContentEditable || /^(input|textarea|select)$/i.test(el.tagName || ''))) return;

			const key = (e.key || '').toLowerCase();
			if (key === 'z' && !e.shiftKey) {
				e.preventDefault();
				handleUndo();
			} else if (key === 'y' || (key === 'z' && e.shiftKey)) {
				// ctrl+shift+z is the other half of the redo convention; without
				// it that chord would fall through to undo, which is worse than
				// doing nothing.
				e.preventDefault();
				handleRedo();
			}
		}

		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
		// history/redoStack: the handlers close over both, so a stale listener
		// would undo from a snapshot of the stacks taken renders ago.
	}, [history, redoStack, colors, productId]);

	// Filters come back from the design column as plain objects - that is how they
	// are stored, and rightly so. But everything that touches them afterwards wants
	// filter instances: Image.toObject calls filterObj.toObject() on the next save,
	// and applyFilters calls into them to draw. Setting the stored objects straight
	// onto the image is what made a reload throw "toObject is not a function" on the
	// save that follows the load - and draw the picture unfiltered until it did.
	function reviveFilters(filters) {
		return new Promise(resolve => {
			if (!Array.isArray(filters) || filters.length === 0) {
				resolve([]);
				return;
			}
			try {
				fabric.util.enlivenObjects(filters, revived => {
					// A filter type this fabric does not know comes back undefined.
					resolve((revived || []).filter(Boolean));
				}, 'fabric.Image.filters');
			} catch (e) {
				// A design whose filters cannot be restored still has to open.
				console.warn('Could not restore the filters on a stored image', e);
				resolve([]);
			}
		});
	}

	const loadSide = async (canvas, elements, side, productId, color) => {
		// Callers resolve the colour from the list themselves, but a name that no
		// longer matches anything used to arrive here as undefined and throw on
		// color[side], taking the whole load down with it.
		if (!color) color = colors[0];
		if (!color) {
			console.warn('loadSide: no colour to draw with', side);
			return;
		}
		const runId = ++loadRunRef.current;
		// True while this run still owns the canvas.
		const current = () => loadRunRef.current === runId;

		// canvas.clear() takes the background with it, and its replacement only
		// arrives a task later - so every reload painted at least one frame with no
		// shirt on it at all. Undo and redo redraw the side that is already on
		// screen, so the picture that was just thrown away is usually the exact one
		// being asked for: hold on to it and put it straight back, in the same tick.
		const backgroundUrl = colorImageUrl(SITE_URL, color[side]);
		const previous = canvas.backgroundImage;
		const reusable = previous && typeof previous.getElement === 'function' &&
			previous.getElement() && previous.getElement().src === backgroundUrl
			? previous
			: null;

    	canvas.clear();

		if (reusable) {
			canvas.setBackgroundImage(reusable, canvas.renderAll.bind(canvas));
		} else {
    	// Set background image for this side
    	await new Promise(resolve => {
        	fabric.Image.fromURL(
            	backgroundUrl,
            	img => {
					// A run that has been overtaken must not put its shirt back on
					// a canvas the newer one has already drawn.
					if (!current()) { resolve(); return; }
                	canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas), {
                    		scaleX: canvas.width / img.width,
                    		scaleY: canvas.height / img.height
                	});
					//console.log(img);
					if(window.innerWidth < 600){
						//console.log("tada");
						if(!cropBoxDone){
							const cropBox = getCropBox(img);
							const container = document.querySelector("div.canvas-container");
							if (cropBox && container) {
								// The crop box is in image pixels; the container is drawn at
								// whatever size the CSS gives it. Work in on-screen pixels.
								const rect = container.getBoundingClientRect();
								const pxPerImgX = rect.width / img.width;
								const pxPerImgY = rect.height / img.height;
								const boxLeft = cropBox.x * pxPerImgX;
								const boxWidth = cropBox.width * pxPerImgX;
								const boxTop = cropBox.y * pxPerImgY;
								const boxHeight = cropBox.height * pxPerImgY;
								// Fill the screen width with the shirt, less the padding,
								// unless that makes it taller than the frame.
								const scale = Math.min(
									(window.innerWidth - 2 * MOBILE_SHIRT_PADDING_X) / boxWidth,
									(window.innerHeight - 2 * MOBILE_SHIRT_PADDING_Y) / boxHeight
								);
								// Scaled from the top edge, the box's centre ends up at
								// rect.top + scale * (its offset); move that to the middle of
								// the frame. It used to be pinned 20px below the top.
								const y = rect.top + scale * (boxTop + boxHeight / 2) - window.innerHeight / 2;
								// Same horizontally, about the container's centre line: the
								// shirt is not always in the middle of its picture, and
								// scaling about the picture's centre pushed an off-centre one
								// further to the side the more it was zoomed.
								const x = rect.left + rect.width / 2
									+ scale * (boxLeft + boxWidth / 2 - rect.width / 2)
									- window.innerWidth / 2;
								container.style.transform = `translate(${-x}px, ${-y}px) scale(${scale})`;
								container.style.transformOrigin = "top center";
							}
							setCropBoxDone(true);
						}
					}
					resolve();
            	},
            	{ crossOrigin: 'anonymous' }
        	);
    	});
		}
		if (!current()) return;

		const bboxColor = contrastColorForShirt(color.color);
		// Deliberately not bboxColor: the boxes flip at the midpoint, the selection
		// border only gives up white once white is genuinely hard to see. Set before
		// the elements are added below, because setCustomControls reads it as each
		// one lands on the canvas.
		selectionBorderColorRef.current = selectionBorderColorForShirt(color.color);

		snapLineHorizontal.set({ stroke: bboxColor });
		snapLineVertical.set({ stroke: bboxColor });

		canvas.add(snapLineVertical);
		canvas.add(snapLineHorizontal);

		const boundingBoxAddBtns = [];

    	// Draw bounding boxes
		//console.log(currentSideRef.current, boundingBoxes[currentSideRef.current]);

		// The label sits directly on the garment, so it takes the same colour as the
		// bounding boxes - white text disappeared on light shirts.
		const boundingBoxSvg = '<svg width="158" height="37" viewBox="0 0 158 37" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="36" height="36" rx="18" fill="white"/><rect x="0.5" y="0.5" width="36" height="36" rx="18" stroke="#DEE1EA"/><path d="M18.2656 9.33496V27.1958" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M9.33496 18.2109H27.1958" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>			<path d="M47.128 15.6H50.8C51.696 15.6 52.492 15.776 53.188 16.128C53.884 16.472 54.424 16.964 54.808 17.604C55.192 18.236 55.384 18.968 55.384 19.8C55.384 20.632 55.192 21.368 54.808 22.008C54.424 22.64 53.884 23.132 53.188 23.484C52.492 23.828 51.696 24 50.8 24H47.128V15.6ZM50.728 22.68C51.344 22.68 51.884 22.564 52.348 22.332C52.82 22.092 53.18 21.756 53.428 21.324C53.684 20.884 53.812 20.376 53.812 19.8C53.812 19.224 53.684 18.72 53.428 18.288C53.18 17.848 52.82 17.512 52.348 17.28C51.884 17.04 51.344 16.92 50.728 16.92H48.688V22.68H50.728ZM62.9221 20.832C62.9221 20.936 62.9141 21.084 62.8981 21.276H57.8701C57.9581 21.748 58.1861 22.124 58.5541 22.404C58.9301 22.676 59.3941 22.812 59.9461 22.812C60.6501 22.812 61.2301 22.58 61.6861 22.116L62.4901 23.04C62.2021 23.384 61.8381 23.644 61.3981 23.82C60.9581 23.996 60.4621 24.084 59.9101 24.084C59.2061 24.084 58.5861 23.944 58.0501 23.664C57.5141 23.384 57.0981 22.996 56.8021 22.5C56.5141 21.996 56.3701 21.428 56.3701 20.796C56.3701 20.172 56.5101 19.612 56.7901 19.116C57.0781 18.612 57.4741 18.22 57.9781 17.94C58.4821 17.66 59.0501 17.52 59.6821 17.52C60.3061 17.52 60.8621 17.66 61.3501 17.94C61.8461 18.212 62.2301 18.6 62.5021 19.104C62.7821 19.6 62.9221 20.176 62.9221 20.832ZM59.6821 18.72C59.2021 18.72 58.7941 18.864 58.4581 19.152C58.1301 19.432 57.9301 19.808 57.8581 20.28H61.4941C61.4301 19.816 61.2341 19.44 60.9061 19.152C60.5781 18.864 60.1701 18.72 59.6821 18.72ZM66.3429 24.084C65.8229 24.084 65.3149 24.016 64.8189 23.88C64.3229 23.744 63.9269 23.572 63.6309 23.364L64.2069 22.224C64.4949 22.416 64.8389 22.572 65.2389 22.692C65.6469 22.804 66.0469 22.86 66.4389 22.86C67.3349 22.86 67.7829 22.624 67.7829 22.152C67.7829 21.928 67.6669 21.772 67.4349 21.684C67.2109 21.596 66.8469 21.512 66.3429 21.432C65.8149 21.352 65.3829 21.26 65.0469 21.156C64.7189 21.052 64.4309 20.872 64.1829 20.616C63.9429 20.352 63.8229 19.988 63.8229 19.524C63.8229 18.916 64.0749 18.432 64.5789 18.072C65.0909 17.704 65.7789 17.52 66.6429 17.52C67.0829 17.52 67.5229 17.572 67.9629 17.676C68.4029 17.772 68.7629 17.904 69.0429 18.072L68.4669 19.212C67.9229 18.892 67.3109 18.732 66.6309 18.732C66.1909 18.732 65.8549 18.8 65.6229 18.936C65.3989 19.064 65.2869 19.236 65.2869 19.452C65.2869 19.692 65.4069 19.864 65.6469 19.968C65.8949 20.064 66.2749 20.156 66.7869 20.244C67.2989 20.324 67.7189 20.416 68.0469 20.52C68.3749 20.624 68.6549 20.8 68.8869 21.048C69.1269 21.296 69.2469 21.648 69.2469 22.104C69.2469 22.704 68.9869 23.184 68.4669 23.544C67.9469 23.904 67.2389 24.084 66.3429 24.084ZM70.5387 17.592H72.0387V24H70.5387V17.592ZM71.2947 16.536C71.0227 16.536 70.7947 16.452 70.6107 16.284C70.4267 16.108 70.3347 15.892 70.3347 15.636C70.3347 15.38 70.4267 15.168 70.6107 15C70.7947 14.824 71.0227 14.736 71.2947 14.736C71.5667 14.736 71.7947 14.82 71.9787 14.988C72.1627 15.148 72.2547 15.352 72.2547 15.6C72.2547 15.864 72.1627 16.088 71.9787 16.272C71.8027 16.448 71.5747 16.536 71.2947 16.536ZM80.3674 17.592V23.028C80.3674 25.284 79.2154 26.412 76.9114 26.412C76.2954 26.412 75.7114 26.332 75.1594 26.172C74.6074 26.02 74.1514 25.796 73.7914 25.5L74.4634 24.372C74.7434 24.604 75.0954 24.788 75.5194 24.924C75.9514 25.068 76.3874 25.14 76.8274 25.14C77.5314 25.14 78.0474 24.98 78.3754 24.66C78.7034 24.34 78.8674 23.852 78.8674 23.196V22.86C78.6114 23.14 78.2994 23.352 77.9314 23.496C77.5634 23.64 77.1594 23.712 76.7194 23.712C76.1114 23.712 75.5594 23.584 75.0634 23.328C74.5754 23.064 74.1874 22.696 73.8994 22.224C73.6194 21.752 73.4794 21.212 73.4794 20.604C73.4794 19.996 73.6194 19.46 73.8994 18.996C74.1874 18.524 74.5754 18.16 75.0634 17.904C75.5594 17.648 76.1114 17.52 76.7194 17.52C77.1834 17.52 77.6034 17.596 77.9794 17.748C78.3634 17.9 78.6834 18.132 78.9394 18.444V17.592H80.3674ZM76.9474 22.44C77.5154 22.44 77.9794 22.272 78.3394 21.936C78.7074 21.592 78.8914 21.148 78.8914 20.604C78.8914 20.068 78.7074 19.632 78.3394 19.296C77.9794 18.96 77.5154 18.792 76.9474 18.792C76.3714 18.792 75.8994 18.96 75.5314 19.296C75.1714 19.632 74.9914 20.068 74.9914 20.604C74.9914 21.148 75.1714 21.592 75.5314 21.936C75.8994 22.272 76.3714 22.44 76.9474 22.44ZM85.9755 17.52C86.7835 17.52 87.4315 17.756 87.9195 18.228C88.4075 18.7 88.6515 19.4 88.6515 20.328V24H87.1515V20.52C87.1515 19.96 87.0195 19.54 86.7555 19.26C86.4915 18.972 86.1155 18.828 85.6275 18.828C85.0755 18.828 84.6395 18.996 84.3195 19.332C83.9995 19.66 83.8395 20.136 83.8395 20.76V24H82.3395V17.592H83.7675V18.42C84.0155 18.124 84.3275 17.9 84.7035 17.748C85.0795 17.596 85.5035 17.52 85.9755 17.52ZM97.7227 23.652C97.5467 23.796 97.3307 23.904 97.0747 23.976C96.8267 24.048 96.5627 24.084 96.2827 24.084C95.5787 24.084 95.0347 23.9 94.6507 23.532C94.2667 23.164 94.0747 22.628 94.0747 21.924V18.84H93.0187V17.64H94.0747V16.176H95.5747V17.64H97.2907V18.84H95.5747V21.888C95.5747 22.2 95.6507 22.44 95.8027 22.608C95.9547 22.768 96.1747 22.848 96.4627 22.848C96.7987 22.848 97.0787 22.76 97.3027 22.584L97.7227 23.652ZM101.626 24.084C100.978 24.084 100.394 23.944 99.8737 23.664C99.3537 23.384 98.9457 22.996 98.6497 22.5C98.3617 21.996 98.2177 21.428 98.2177 20.796C98.2177 20.164 98.3617 19.6 98.6497 19.104C98.9457 18.608 99.3537 18.22 99.8737 17.94C100.394 17.66 100.978 17.52 101.626 17.52C102.282 17.52 102.87 17.66 103.39 17.94C103.91 18.22 104.314 18.608 104.602 19.104C104.898 19.6 105.046 20.164 105.046 20.796C105.046 21.428 104.898 21.996 104.602 22.5C104.314 22.996 103.91 23.384 103.39 23.664C102.87 23.944 102.282 24.084 101.626 24.084ZM101.626 22.8C102.178 22.8 102.634 22.616 102.994 22.248C103.354 21.88 103.534 21.396 103.534 20.796C103.534 20.196 103.354 19.712 102.994 19.344C102.634 18.976 102.178 18.792 101.626 18.792C101.074 18.792 100.618 18.976 100.258 19.344C99.9057 19.712 99.7297 20.196 99.7297 20.796C99.7297 21.396 99.9057 21.88 100.258 22.248C100.618 22.616 101.074 22.8 101.626 22.8ZM112.504 20.832C112.504 20.936 112.496 21.084 112.48 21.276H107.452C107.54 21.748 107.768 22.124 108.136 22.404C108.512 22.676 108.976 22.812 109.528 22.812C110.232 22.812 110.812 22.58 111.268 22.116L112.072 23.04C111.784 23.384 111.42 23.644 110.98 23.82C110.54 23.996 110.044 24.084 109.492 24.084C108.788 24.084 108.168 23.944 107.632 23.664C107.096 23.384 106.68 22.996 106.384 22.5C106.096 21.996 105.952 21.428 105.952 20.796C105.952 20.172 106.092 19.612 106.372 19.116C106.66 18.612 107.056 18.22 107.56 17.94C108.064 17.66 108.632 17.52 109.264 17.52C109.888 17.52 110.444 17.66 110.932 17.94C111.428 18.212 111.812 18.6 112.084 19.104C112.364 19.6 112.504 20.176 112.504 20.832ZM109.264 18.72C108.784 18.72 108.376 18.864 108.04 19.152C107.712 19.432 107.512 19.808 107.44 20.28H111.076C111.012 19.816 110.816 19.44 110.488 19.152C110.16 18.864 109.752 18.72 109.264 18.72ZM119.828 17.592L117.08 24H115.532L112.784 17.592H114.344L116.336 22.344L118.388 17.592H119.828ZM123.469 24.084C122.821 24.084 122.237 23.944 121.717 23.664C121.197 23.384 120.789 22.996 120.493 22.5C120.205 21.996 120.061 21.428 120.061 20.796C120.061 20.164 120.205 19.6 120.493 19.104C120.789 18.608 121.197 18.22 121.717 17.94C122.237 17.66 122.821 17.52 123.469 17.52C124.125 17.52 124.713 17.66 125.233 17.94C125.753 18.22 126.157 18.608 126.445 19.104C126.741 19.6 126.889 20.164 126.889 20.796C126.889 21.428 126.741 21.996 126.445 22.5C126.157 22.996 125.753 23.384 125.233 23.664C124.713 23.944 124.125 24.084 123.469 24.084ZM123.469 22.8C124.021 22.8 124.477 22.616 124.837 22.248C125.197 21.88 125.377 21.396 125.377 20.796C125.377 20.196 125.197 19.712 124.837 19.344C124.477 18.976 124.021 18.792 123.469 18.792C122.917 18.792 122.461 18.976 122.101 19.344C121.749 19.712 121.573 20.196 121.573 20.796C121.573 21.396 121.749 21.88 122.101 22.248C122.461 22.616 122.917 22.8 123.469 22.8ZM134.348 20.832C134.348 20.936 134.34 21.084 134.324 21.276H129.296C129.384 21.748 129.612 22.124 129.98 22.404C130.356 22.676 130.82 22.812 131.372 22.812C132.076 22.812 132.656 22.58 133.112 22.116L133.916 23.04C133.628 23.384 133.264 23.644 132.824 23.82C132.384 23.996 131.888 24.084 131.336 24.084C130.632 24.084 130.012 23.944 129.476 23.664C128.94 23.384 128.524 22.996 128.228 22.5C127.94 21.996 127.796 21.428 127.796 20.796C127.796 20.172 127.936 19.612 128.216 19.116C128.504 18.612 128.9 18.22 129.404 17.94C129.908 17.66 130.476 17.52 131.108 17.52C131.732 17.52 132.288 17.66 132.776 17.94C133.272 18.212 133.656 18.6 133.928 19.104C134.208 19.6 134.348 20.176 134.348 20.832ZM131.108 18.72C130.628 18.72 130.22 18.864 129.884 19.152C129.556 19.432 129.356 19.808 129.284 20.28H132.92C132.856 19.816 132.66 19.44 132.332 19.152C132.004 18.864 131.596 18.72 131.108 18.72ZM142.149 17.592V23.028C142.149 25.284 140.997 26.412 138.693 26.412C138.077 26.412 137.493 26.332 136.941 26.172C136.389 26.02 135.933 25.796 135.573 25.5L136.245 24.372C136.525 24.604 136.877 24.788 137.301 24.924C137.733 25.068 138.169 25.14 138.609 25.14C139.313 25.14 139.829 24.98 140.157 24.66C140.485 24.34 140.649 23.852 140.649 23.196V22.86C140.393 23.14 140.081 23.352 139.713 23.496C139.345 23.64 138.941 23.712 138.501 23.712C137.893 23.712 137.341 23.584 136.845 23.328C136.357 23.064 135.969 22.696 135.681 22.224C135.401 21.752 135.261 21.212 135.261 20.604C135.261 19.996 135.401 19.46 135.681 18.996C135.969 18.524 136.357 18.16 136.845 17.904C137.341 17.648 137.893 17.52 138.501 17.52C138.965 17.52 139.385 17.596 139.761 17.748C140.145 17.9 140.465 18.132 140.721 18.444V17.592H142.149ZM138.729 22.44C139.297 22.44 139.761 22.272 140.121 21.936C140.489 21.592 140.673 21.148 140.673 20.604C140.673 20.068 140.489 19.632 140.121 19.296C139.761 18.96 139.297 18.792 138.729 18.792C138.153 18.792 137.681 18.96 137.313 19.296C136.953 19.632 136.773 20.068 136.773 20.604C136.773 21.148 136.953 21.592 137.313 21.936C137.681 22.272 138.153 22.44 138.729 22.44ZM150.145 20.832C150.145 20.936 150.137 21.084 150.121 21.276H145.093C145.181 21.748 145.409 22.124 145.777 22.404C146.153 22.676 146.617 22.812 147.169 22.812C147.873 22.812 148.453 22.58 148.909 22.116L149.713 23.04C149.425 23.384 149.061 23.644 148.621 23.82C148.181 23.996 147.685 24.084 147.133 24.084C146.429 24.084 145.809 23.944 145.273 23.664C144.737 23.384 144.321 22.996 144.025 22.5C143.737 21.996 143.593 21.428 143.593 20.796C143.593 20.172 143.733 19.612 144.013 19.116C144.301 18.612 144.697 18.22 145.201 17.94C145.705 17.66 146.273 17.52 146.905 17.52C147.529 17.52 148.085 17.66 148.573 17.94C149.069 18.212 149.453 18.6 149.725 19.104C150.005 19.6 150.145 20.176 150.145 20.832ZM146.905 18.72C146.425 18.72 146.017 18.864 145.681 19.152C145.353 19.432 145.153 19.808 145.081 20.28H148.717C148.653 19.816 148.457 19.44 148.129 19.152C147.801 18.864 147.393 18.72 146.905 18.72ZM155.222 17.52C156.03 17.52 156.678 17.756 157.166 18.228C157.654 18.7 157.898 19.4 157.898 20.328V24H156.398V20.52C156.398 19.96 156.266 19.54 156.002 19.26C155.738 18.972 155.362 18.828 154.874 18.828C154.322 18.828 153.886 18.996 153.566 19.332C153.246 19.66 153.086 20.136 153.086 20.76V24H151.586V17.592H153.014V18.42C153.262 18.124 153.574 17.9 153.95 17.748C154.326 17.596 154.75 17.52 155.222 17.52Z" fill="' + bboxColor + '"/></svg>';
		const boundingBoxSvgWithouthText = `<svg width="37" height="37" viewBox="0 0 37 37" fill="none" xmlns="http://www.w3.org/2000/svg">
			<rect x="0.5" y="0.5" width="36" height="36" rx="18" fill="white"/>
			<rect x="0.5" y="0.5" width="36" height="36" rx="18" stroke="#DEE1EA"/>
			<path d="M18.2656 9.33496V27.1958" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
			<path d="M9.33496 18.2109H27.1958" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
			</svg>`;

		// Boxes that overlap or touch are one printable region to the user: it is
		// drawn as a single outline around their union - no internal edges - and
		// gets one add button, in the middle of its biggest box. Boxes with a gap
		// between them stay separate regions and are drawn as before.
		const boxGroups = groupBoxes(boundingBoxes[currentSideRef.current]);

		boxGroups.forEach(group => {
			const merged = group.boxes.length > 1;

			if (merged) {
				group.outline.forEach(seg => {
					const line = new fabric.Line([seg.x1, seg.y1, seg.x2, seg.y2], {
						stroke: bboxColor,
						strokeDashArray: [5,5],
						selectable: false,
						evented: false,
						type: 'boundingBoxOutline',
						padding: 0
					});
					canvas.add(line);
					line.sendToBack();
				});
			}

			group.boxes.forEach(box => {
				const rect = new fabric.Rect({
					id: box.id,
					// The region, not the box, is what hover and insert act on.
					regionId: group.largest.id,
					regionBox: group.largest,
					left: box.left,
					top: box.top,
					width: box.width,
					height: box.height,
					fill: 'rgba(255,0,0,0)',
					// In a merged region the box only carries the hit area; the
					// outline above draws the region.
					stroke: merged ? 'rgba(0,0,0,0)' : bboxColor,
					selectable: false,
					evented: true,
					strokeDashArray: [5,5],
					type: 'boundingBox',
					padding: 0
				});
				canvas.add(rect);
				rect.sendToBack();
			});

			if(window.innerWidth >= 600){
				const box = group.largest;
				let svgString = boundingBoxSvg;
				let svgOffsetX = 79;
				if(box.width < 158){
					svgString = boundingBoxSvgWithouthText;
					svgOffsetX = 18;
				}
				fabric.loadSVGFromString(svgString, function (objects, options) {
					const img = fabric.util.groupSVGElements(objects, options);
					// Transparent to the mouse on purpose. The label is added after the
					// print areas and so sits on top of them, and while it was evented
					// fabric handed it every pointer event that landed on it instead of
					// the boundingBox rect underneath: hovering it gave the move cursor
					// its hoverCursor defaults to, took the "+" cursor down, and a press
					// on the very thing inviting the customer to add a design closed the
					// menu rather than opening it. It is a label, not a button - the
					// print area behind it is what handles both.
					img.set({ type: 'boundingBoxAddBtn', parent: box.id, left: box.left + (box.width / 2) - svgOffsetX, top: box.top + (box.height / 2 ) - 18, scaleX: 1, scaleY: 1, selectable: false, evented: false, visible: false });
					img.setCoords();
					boundingBoxAddBtns.push(img);
					//canvas.add(img);
				});
			}
    	});
		
		if(window.innerWidth >= 600){
			if(showBoundingBoxAddBtnTimerRef.current != null){
				clearTimeout(showBoundingBoxAddBtnTimerRef.current);
			}
			
			//console.log(elements.length);

			if(elements.length < 2){
				setShowBoundingBoxAddBtnTimer(setTimeout(function(){
					// Three seconds is long enough for several undos to have gone
					// past, and the ref this timer is cancelled through only catches
					// up a render later - so check ownership here too rather than
					// stacking a set of buttons per run.
					if (!current()) return;
					// Added hidden while the parent's menu is open; menuClosed
					// brings them up once the customer is done there.
					boundingBoxAddBtns.forEach(btn => { btn.set({ visible: !menuOpenRef.current }); canvas.add(btn); });
					canvas.renderAll();
				}, 3000));
			}
		}
		

    	 // Add elements in order, waiting for each image/font to load before adding the next
		 for (const el of elements) {
 			if (el.type === 'text') {
 				// Wait for font to be loaded if specified
 				let font = el.fontFamily || 'Arial';
 				try {
 					await document.fonts.load(`1em '${font}'`);
 				} catch (e) {}
				if (!current()) return;
 				const text = new fabric.Text(el.text, el);
 				setCustomControls(text);
 				canvas.add(text);
 			} else if (el.type === 'image') {
 				await new Promise(resolve => {
// 					console.log('Loading image', el);
					if(!el.src){
						// Nothing to rebuild from - skip rather than stall the loop.
						console.warn('Skipping image element without src', el);
						resolve();
						return;
					}
 					if(el.src.indexOf('.svg') !== -1){
 						// Fetch SVG and use loadSVGFromString to avoid cross-origin taint
 						fetch(el.src).then(res => res.text()).then(svgText => {
 							fabric.loadSVGFromString(svgText, (objects, options) => {
 								const img = fabric.util.groupSVGElements(objects, options);
 								img.set(el);
 								//console.log(img);
 								//console.log(getCropBox(img));
 								setCustomControls(img);
								//img.applyFilters();
 								if (current()) canvas.add(img);
 								resolve();
 							});
 						}).catch(err => {
 							console.warn('Failed to fetch SVG in loadSide:', err);
 							// fallback: try loading via URL
 							fabric.loadSVGFromURL(el.src, (objects, options) => {
 								const img = fabric.util.groupSVGElements(objects, options);
 								img.set(el);
 								setCustomControls(img);
 								if (current()) canvas.add(img);
 								resolve();
 							});
 						});
 					}else{
 						fabric.Image.fromURL(el.src, async img => {
 							//console.log('Loaded image', el);
 							img.set(el);

							// set() has just put the stored plain objects on the
							// image; swap them for instances and draw them, since
							// loading an element does not apply filters by itself.
							img.filters = await reviveFilters(el.filters);
							if (img.filters.length) {
								try {
									img.applyFilters();
								} catch (e) {
									console.warn('Could not apply the stored filters', e);
									img.filters = [];
								}
							}

 							setCustomControls(img);
 							if (current()) canvas.add(img);
							resolve();
 						}, { crossOrigin: 'anonymous' });
 					}
 				});
				if (!current()) return;
 			}
 		}

		if (!current()) return;

		// Before the history snapshot, so an undo back to here lands on the
		// repaired design rather than on the one that was outside the print area.
		enforceAllInRegion(canvas, side);

		if(isRestoringHistory.current){
			isRestoringHistory.current = false;
		}else{
			saveHistory();
		}

		window.parent.postMessage({ action: 'activeSide', value: sides.indexOf(side) }, '*');

    	gridToolsInstanceRef.current.drawGrid(canvas, gridEnabled, gridSize);
    	canvas.renderAll();
		// The side is complete: what is on the canvas is now worth saving. Only a
		// run that got this far says so - an overtaken one returned above, and the
		// newer run that passed it is the one still owing an answer.
		loadSettledRef.current = runId;
		// Save the colour this side was actually drawn with. Every load ends
		// here, so this write reaches the database on every page view: reading
		// the ref instead let a colour that had not finished being applied yet
		// be persisted over the stored one, permanently.
    	handleSave(color.color);
	};

	// Redraw the thumbnails of every side but `side` on the shirt in `color`.
	//
	// `sidesData` is the design to draw from; it defaults to the ref, which a
	// caller that has only just called setDesigns must not rely on - the ref
	// follows the state a render later.
	//
	// Two runs can overlap - a colour picked twice in a row, or a colour arriving
	// while the stored design is still being fetched - and they finish one side
	// at a time, so whichever wrote a side last would win: a random subset of
	// thumbnails on the old colour. Only the newest run may write anything.
	const makeOtherSidesThumbnails = async function(side, color, sidesData){
		const runId = ++thumbRunRef.current;
		const current = () => thumbRunRef.current === runId;
		const data = sidesData || designsRef.current;
		let wrote = false;
		for(const key in data){
			if(key !== side){
				if (!current()) return;
				const canvasEle = document.createElement('canvas');
				const canvas = new fabric.Canvas(canvasEle, {
					height: 900,
					width: 950,
					//backgroundColor: '#f0f0f0',
					selection: false, // enables selection box and controls
					perPixelTargetFind: false // use bounding box for selection
				});
				await new Promise(resolve => {
        			fabric.Image.fromURL(colorImageUrl(SITE_URL, color[key]), img => {
                		canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas), {
                    		scaleX: canvas.width / img.width,
                    		scaleY: canvas.height / img.height
                		});
						resolve();
            		},
            		{ crossOrigin: 'anonymous' });
    			});
		
				//console.log(key, designsRef.current[key]);

		 		// Add elements in order, waiting for each image/font to load before adding the next
		 		for (const el of data[key]) {
 					if (el.type === 'text') {
 						// Wait for font to be loaded if specified
 						let font = el.fontFamily || 'Arial';
 						try {
 							await document.fonts.load(`1em '${font}'`);
 						} catch (e) {}
 						const text = new fabric.Text(el.text, el);
 						//setCustomControls(text);
 						canvas.add(text);
 					} else if (el.type === 'image') {
 						await new Promise(resolve => {
							if(!el.src){
								console.warn('Skipping image element without src', el);
								resolve();
								return;
							}
							if(el.src.indexOf('.svg') !== -1){
 								// Fetch SVG and use loadSVGFromString to avoid cross-origin taint
 								fetch(el.src).then(res => res.text()).then(svgText => {
 									fabric.loadSVGFromString(svgText, (objects, options) => {
 										const img = fabric.util.groupSVGElements(objects, options);
 										img.set(el);
 										//setCustomControls(img);
										canvas.add(img);
 										resolve();
 									});
 								}).catch(err => {
 									console.warn('Failed to fetch SVG in loadSide:', err);
 									// fallback: try loading via URL
 									fabric.loadSVGFromURL(el.src, (objects, options) => {
 										const img = fabric.util.groupSVGElements(objects, options);
 										img.set(el);
 										//setCustomControls(img);
 										canvas.add(img);
 										resolve();
 									});
 								});
							}else{
								fabric.Image.fromURL(el.src, img => {
									//console.log('Loaded image', el);
									img.set(el);
									//setCustomControls(img);
									canvas.add(img);
									resolve();
								}, { crossOrigin: 'anonymous' });
							}
						});
 					}
				}
				// Overtaken while the side was loading: a newer run owns the
				// thumbnails now, and this one's would be on the wrong colour.
				if (!current()) { canvas.dispose(); return; }
				const thumbnail = canvas.toDataURL({ format: 'png', quality: 0.8, multiplier: 1 });
				//const updatedDesigns = { ...designsRef.current, [key]: [{ type: 'thumbnail', thumbnail }] };
				const json = serializeSideObjects(canvas);
 				json.push({ type: 'thumbnail', thumbnail });
				// The ref moves with the state, not a render behind it: the save at
				// the end of loadSide reads the ref, and with the ref lagging it
				// persisted the old thumbnails under the new colour - which the
				// next page view then showed as the stored design.
				designsRef.current = { ...designsRef.current, [key]: json };
				setDesigns(prev => ({ ...prev, [key]: json }));
				wrote = true;
				//window.parent.postMessage({ action: 'updateThumbnail', value: { side: key, thumbnail } }, '*');
				canvas.dispose();
			}
		}
		// Persist what was drawn. Not through handleSave: that re-captures the
		// main canvas, which loadSide may still be filling. The thumbnail of the
		// current side in the ref may be the old one at this point; loadSide's own
		// save follows with the right one, and by then the ref holds these too.
		if (wrote && current()) {
			await fetch('/api/save-design', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ userId, productId, sides: designsRef.current, selectedShirtColor: color.color })
			});
		}
	};

  	const handleSideChange = (side) => {
		const canvas = canvasRef.current;
		// Only worth keeping if the side it came from had finished drawing. Leave
		// during a load - the page has only just been refreshed, say - and the
		// canvas holds however much of it had arrived, so this would store a
		// half-built design, or an empty one, over what the customer had.
		if (!loadInFlight()) saveCurrentSide(currentSideRef.current);
		canvas.clear();
		const colorObj = findColor(colors, selectedShirtColorRef.current);
		// Now, not a render later when the effect on [currentSide] gets to it:
		// anything still in flight - the load-design fetch above all - reads this
		// ref to find out which side it came back to, and a stale answer sends it
		// drawing the old side's design onto this one.
		currentSideRef.current = side;
		setCurrentSide(side);

		//const json = canvas.toDatalessJSON(['text', 'url']);
		//json.objects = json.objects.filter(obj => obj.type === 'text' || obj.type === 'image');
		setHistory([]);
		setRedoStack([]);
		window.parent.postMessage({ action: 'redo', value: 0 }, '*');
		window.parent.postMessage({ action: 'undo', value: 0 }, '*');

		if (colorObj) {
			loadSide(canvas, designsRef.current[side] || [], side, productId, colorObj);
		}
	};

	// Serialise a canvas the way the design column expects: only the elements the
	// customer added. An SVG is stored as a group tagged type 'image', so drop its
	// shape tree - loadSide rebuilds that from src and keeping it only bloats the
	// saved design.
	function serializeSideObjects(canvas) {
		return canvas.getObjects()
			.filter(obj => obj.type === 'text' || obj.type === 'image')
			.map(obj => {
				const o = obj.toObject(['text', 'src']);
				if (typeof o.src === 'string' && o.src.indexOf('.svg') !== -1) {
					delete o.objects;
					delete o.path;
				}
				// The resample filter lives on the prototype, so toObject serialises
				// it for every image. Storing it would hand loadSide a plain object
				// where a filter instance is expected and break rendering on reload.
				delete o.resizeFilter;
				return o;
			});
	}

	const saveCurrentSide = (side) => {
 		const canvas = canvasRef.current;

		// Hide bounding boxes, add buttons and centring guides for thumbnail capture
		const boundingBoxObjects = canvas.getObjects().filter(obj => obj.type === 'boundingBox' || obj.type === 'boundingBoxOutline' || obj.type === 'boundingBoxAddBtn' || obj.id === 'snapLineVertical' || obj.id === 'snapLineHorizontal');
		const visibilityStates = boundingBoxObjects.map(obj => obj.visible);
		boundingBoxObjects.forEach(obj => obj.set({ visible: false }));
		canvas.renderAll();

 		const thumbnail = canvas.toDataURL({ format: 'png', quality: 0.8, multiplier: 1 });

		// Restore visibility
		boundingBoxObjects.forEach((obj, idx) => obj.set({ visible: visibilityStates[idx] }));
		canvas.renderAll();

 		const json = serializeSideObjects(canvas);
 		json.push({ type: 'thumbnail', thumbnail });
		const updatedDesigns = { ...designsRef.current, [side]: json };
		// Move the ref now as well: a save that runs before the next render -
		// the one makeOtherSidesThumbnails ends with - must see this side too.
		designsRef.current = updatedDesigns;
		setDesigns(prev => ({ ...prev, [side]: json }));
		return { designs: updatedDesigns, selectedShirtColor: selectedShirtColorRef.current };
	  };

  	const handleSave = async (colorOverride) => {
		const canvas = canvasRef.current;
		// If multiple objects are selected, ungroup them before saving
		if (canvas.getActiveObject() && canvas.getActiveObject().type === 'activeSelection') {
			//canvas.getActiveObject().toGroup(); // This will convert activeSelection to a group
			//canvas.getActiveObject().unselect(); // Deselect to finalize positions
			canvas.discardActiveObject(); // This will ungroup and place objects back on canvas
		}
		const { designs: updatedDesigns } = saveCurrentSide(currentSideRef.current);
		
		if(updatedDesigns[currentSideRef.current]){
			const objects = updatedDesigns[currentSideRef.current].find(obj => obj.type === 'image' || obj.type === 'text');
			showBoundingBoxAddLabels(!objects || objects.length === 0 && showBoundingBoxAddBtnTimerRef.current == null);
		}
		
		const colorToSave = colorOverride || selectedShirtColorRef.current;
		await fetch('/api/save-design', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ userId, productId, sides: updatedDesigns, selectedShirtColor: colorToSave })
		});
		sendStatusToParent(updatedDesigns);
		sendContrastToParent();
	};

	// Warn when a text colour disappears into the shirt behind it. Shirts are not
	// one flat colour - panels, stripes and prints mean the same text can read fine
	// over one part and vanish over another - so this samples the shirt pixels
	// underneath the glyphs rather than comparing against the chosen colour name.
	//
	// Sent from handleSave because every relevant change already passes through it:
	// adding or editing text (including the colour), moving and scaling it, and the
	// reload at the end of loadSide that follows a side or shirt-colour switch.
	function sendContrastToParent() {
		const contrastTools = contrastToolsInstanceRef.current;
		if (!contrastTools) return;
		let warnings = [];
		try {
			warnings = contrastTools.check();
		} catch (e) {
			// A readability hint is never worth breaking a save over.
			console.warn('Could not run the text contrast check:', e);
			return;
		}
		// Posted on every save, including when it is empty, so the parent knows when
		// to take a warning down again. It used to be sent only when there was
		// something to say, which is the half that leaves a warning standing after
		// the customer has already fixed the colour.
		window.parent.postMessage({
			action: 'textContrastWarning',
			value: { side: currentSideRef.current, warnings }
		}, '*');
	}

  	// Keep the ref updated with the latest handleSave so the stable wrapper forwards correctly
	useEffect(() => {
		handleSaveRef.current = handleSave;
	}, [handleSave]);

	function handleSelection(e) {
		//console.log('Selection event:', e);
		if (!e.selected || e.selected.length === 0) {
			setSelectedObject(null);
			return;
		}
		if (e.selected.length === 1) {
			const obj = e.selected[0];
			// Let Fabric manage active object; sync React state and the TextTools instance
			setSelectedObject(obj);
			if(obj.type === 'text'){
				if (textToolsInstanceRef.current && typeof textToolsInstanceRef.current.setSelectedObject === 'function') {
					textToolsInstanceRef.current.setSelectedObject(obj);
				}
			}
			if(obj.type === 'image'){
				if (imageToolsInstanceRef.current && typeof imageToolsInstanceRef.current.setSelectedObject === 'function') {
					// Selects, and nothing more: the parent hears about the image
					// when "Afbeelding aanpassen" on the element toolbar is pressed.
					// The single exception is the selection that follows an upload,
					// which ImageTools recognises on its own and does report.
					imageToolsInstanceRef.current.setSelectedObject(obj);
				}
			}
		} else {
			// Multiple selection: mirror activeSelection object into React state and TextTools
			if (e.target && e.target.type === 'activeSelection') {
				setSelectedObject(e.target);
				if (textToolsInstanceRef.current && typeof textToolsInstanceRef.current.setSelectedObject === 'function') {
					textToolsInstanceRef.current.setSelectedObject(e.target);
				}
			}
		}
  	}
	
	function deleteObject(eventData, transform) {
		const canvas = transform.target.canvas;
		canvas.remove(transform.target);
		const type = transform.target.type;
		if(type === 'text'){
			window.parent.postMessage({ action: 'textDeleted', value: '' }, '*');
		}
		Logger(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted`);
		canvas.discardActiveObject();
		setSelectedObject(null);
		canvas.renderAll();
		handleSave();
	}
	
	function cloneObject(_eventData, transform) {
		const canvas = transform.target.canvas;
		transform.target.clone((cloned) => {
			//console.log(cloned);
			cloned.left += CLONE_OFFSET;
			cloned.top += CLONE_OFFSET;
			setCustomControls(cloned);
			canvas.add(cloned);

			// A bigger step is easier to grab but can drop the copy outside the print
			// area, which a drag would never allow. Pull it back in the same way, with
			// no last valid pose to fall back on: that would put the copy exactly on
			// top of the original, undoing the offset. Without one the snap picks the
			// nearest centre that fits, so the copy stays where it was headed.
			enforceObjectInRegion(getRegion(), cloned);
			if (isObjectInsideRegion(cloned)) cloned.__lastValidPose = capturePose(cloned);

			// Hand over the copy selected, so it can be moved or restyled right away
			// instead of having to pick it out from the original underneath it.
			// setActiveObject fires the selection events, so the React state and the
			// text/image tool instances follow along on their own.
			canvas.setActiveObject(cloned);

			const type = transform.target.type;
			Logger(`${type.charAt(0).toUpperCase() + type.slice(1)} cloned`);
			canvas.renderAll();
			handleSave();
		});
	}
	
	function renderIcon(icon) {
		return function (ctx, left, top, _styleOverride, fabricObject) {
			const size = this.cornerSize;
			ctx.save();
			ctx.translate(left, top);
			//ctx.rotate(-fabric.util.degreesToRadians(fabricObject.angle));
			//ctx.rotate(0);
			ctx.drawImage(icon, -size / 2, -size / 2, size, size);
			ctx.restore();
		};
	}
	
	function getCropBox(img) {
		try{
			const canvas = document.createElement('canvas');
			let width = img.width;
			let height = img.height;

			//console.log(width, height);
			canvas.width = width;
			canvas.height = height;

			const ctx = canvas.getContext('2d');
			
			//console.log(img.getElement());
	        ctx.drawImage(img.getElement(), 0, 0);
	        const data = ctx.getImageData(0, 0, width, height).data;
			//console.log(data);
	
			let minX = width;
		    let minY = height;
		    let maxX = 0;
		    let maxY = 0;

			const h_step = 1;//Math.floor(height / 6);
			const w_step = 1;//Math.floor(width / 6);

			//let str_img = "";
	
		    for (let y = 0; y < height; y++) {
				for (let x = 0; x < width; x++) {
		            const index = (y * width + x) * 4;
		            const alpha = data[index + 3];
					//console.log(x, y, alpha);
	
		            if (alpha !== 0) { // Non‑transparent pixel
						//console.log(x, y, alpha, index);
						//return null;
						//str_img += "1";
		                if (x < minX) minX = x;
		                if (y < minY) minY = y;
		                if (x > maxX) maxX = x;
		                if (y > maxY) maxY = y;
					}else{
						//str_img += "0";
					}
				}
				//str_img += "\n";
		    }

			//console.log(str_img);
	
		    // If the image is fully transparent
		    if (minX > maxX || minY > maxY) {
		        return null;
		    }
	
		    return {
		        x: minX,
		        y: minY,
		        width: maxX - minX + 1,
		        height: maxY - minY + 1
		    };
		}catch(e){
			console.warn('Error calculating crop box:', e);
			return null;
		}

	}
	
	// The two buttons on the element toolbar. Both only ask the parent to open a
	// panel - nothing on the canvas changes here.
	function handleEditImage() {
		const tools = imageToolsInstanceRef.current;
		if (tools && typeof tools.postAdvanced === 'function') tools.postAdvanced();
	}

	// The button's own position goes along with it, in the coordinates of this
	// frame's viewport - what the parent needs to open its panel beside the button
	// the customer just pressed.
	function handleRemoveBackground(e) {
		const tools = imageToolsInstanceRef.current;
		if (!tools || typeof tools.postRemoveBackground !== 'function') return;
		const rect = e && e.currentTarget && e.currentTarget.getBoundingClientRect();
		tools.postRemoveBackground(rect ? { left: rect.left, top: rect.top } : null);
		// The panel is on its way: the add cursor goes down and stays down until the
		// pointer leaves the button, rather than reappearing under the panel.
		hideBoundingBoxAddCursor(e);
	}

	function sendStatusToParent(updatedDesigns) {
		const status = {};
		for(const key in updatedDesigns){
			const design = updatedDesigns[key];
			let numImages = 0;
			let numTexts = 0;
			design.forEach(el => {
                if(el.type === 'image') numImages++;
                if(el.type === 'text'){
					// count each non-empty line as a text element, skip empty lines
					numTexts += (el.text || '').split('\n').filter(line => line.trim() !== '').length;
				}
            });
			status[key] = { "images": numImages, "texts": numTexts };
		}
		window.parent.postMessage({ action: 'statusUpdate', value: status }, '*');
	}

  	return (
		<div className="editorContent" ref={editorContentRef}>
      		<div className='sidesContainer'>
				{colors.length > 0 && sides.map(side => (
					<button key={side} onClick={() => handleSideChange(side)}
					style={{
						background: '#fff',
						padding: 0,
						cursor: 'pointer',
						border: currentSide === side ? '1px solid #D8127D' : 'none',
						borderRadius: '8px',
						width: 122,
						height: 122,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						margin: 0,
					}}
					>
					<span style={{
						display: 'block',
						width: 120,
						height: 120,
						maxHeight: '100%',
						maxWidth: '100%',
						borderRadius: '8px',
						background: '#fff',
						boxSizing: 'border-box',
						overflow: 'hidden',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						position: 'relative'
					}}>
						<img
							src={(designs[side]?.find(obj => obj.type === 'thumbnail')?.thumbnail) || colorImageUrl(SITE_URL, (colors.find(c => c.color === selectedShirtColor) || colors[0])[side])}
							alt={side}
							style={{
								width: 120,
								maxWidth: '100%',
								height: 'auto',
								objectFit: 'cover',
								borderRadius: '8px',
								display: 'block',
								position: 'absolute',
								top: 0,
								left: 0
							}}
						/>
						{/* overlay elements for this side */}
						{/*<div style={{position: 'absolute', top: 0, left: 0, width: 120, height: 120}}>
							{renderElementsForThumbnail(side)}
						</div>*/}
					</span>
				</button>
            	))}
        	</div>
			{/*selectedObject && (
				<div>
					<div style={{ marginTop: -21 }}>
						<button onClick={() => { selectedObject.bringToFront(); canvasRef.current.renderAll(); handleSave(); }}>Bring to Front</button>
						<button onClick={() => { selectedObject.sendToBack(); canvasRef.current.renderAll(); handleSave(); }}>Send to Back</button>
						<button onClick={() => { selectedObject.bringForward(); canvasRef.current.renderAll(); handleSave(); }}>Bring Forward</button>
						<button onClick={() => { selectedObject.sendBackwards(); canvasRef.current.renderAll(); handleSave(); }}>Send Backward</button>
					</div>
          		</div>
        	)*/}
			{/*
			<FPDLoaderTools
				setProductId={setProductId}
				setColors={setColors}
				setSelectedShirtColor={setSelectedShirtColor}
				setDesigns={setDesigns}
				canvas={canvasRef}
				loadSide={loadSide}
				setLoadingFPDData={setLoadingFPDData}
			/>*/}
			<div id="convertColorDiv" style={{ display: 'none' }}></div>
			<canvas id="canvas"></canvas>
			{elementToolbar && (
				<div
					style={{
						position: 'absolute',
						left: elementToolbar.left,
						top: elementToolbar.top,
						// Centred on the element and sitting on the anchor, so the gap
						// updateElementToolbar worked out is the gap that is drawn.
						transform: 'translate(-50%, -100%)',
						width: ELEMENT_TOOLBAR_WIDTH,
						height: ELEMENT_TOOLBAR_HEIGHT,
						boxSizing: 'border-box',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						gap: 4,
						padding: 6,
						background: '#fff',
						borderRadius: ELEMENT_TOOLBAR_HEIGHT / 2,
						boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
						// Under the label, which is drawn after it and may overlap.
						zIndex: 9
					}}
					onMouseEnter={hideBoundingBoxAddCursor}
				>
					<button
						type="button"
						aria-label={ELEMENT_TOOLBAR_HINTS.edit}
						onClick={handleEditImage}
						onMouseEnter={e => showElementToolbarHint(e.currentTarget, ELEMENT_TOOLBAR_HINTS.edit)}
						onMouseLeave={clearControlHint}
						style={ELEMENT_TOOLBAR_BUTTON}
					>
						<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
							<path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
							<path d="M18.4 2.6a2 2 0 0 1 2.8 2.8L12.6 14a2 2 0 0 1-.9.5l-2.9.8.8-2.9a2 2 0 0 1 .5-.9z" />
						</svg>
					</button>
					<button
						type="button"
						aria-label={ELEMENT_TOOLBAR_HINTS.removeBackground}
						onClick={handleRemoveBackground}
						onMouseEnter={e => showElementToolbarHint(e.currentTarget, ELEMENT_TOOLBAR_HINTS.removeBackground)}
						onMouseLeave={clearControlHint}
						style={{ ...ELEMENT_TOOLBAR_BUTTON, background: '#F1F2F6' }}
					>
						<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
							{/* Hatching stands for the transparency left behind. */}
							<defs>
								<clipPath id="removeBackgroundClip">
									<rect x="3" y="3" width="18" height="18" rx="3" />
								</clipPath>
							</defs>
							<g clipPath="url(#removeBackgroundClip)" stroke="#22C55E" strokeWidth="1.5">
								<path d="M-2 10 10 -2M-2 16 16 -2M-2 22 22 -2M4 22 22 4M10 22 22 10M16 22 22 16" />
							</g>
							<rect x="3" y="3" width="18" height="18" rx="3" fill="none" stroke="#22C55E" strokeWidth="2" />
						</svg>
					</button>
				</div>
			)}
			{controlHint && (
				<div
					style={{
						position: 'absolute',
						left: controlHint.left,
						top: controlHint.top,
						// Centred on the control and lifted clear of it. Translating by a
						// percentage of its own size means the label does not have to be
						// measured first, so it never flashes in the wrong place.
						transform: controlHint.below
							? `translate(-50%, ${CONTROL_HINT_GAP}px)`
							: `translate(-50%, calc(-100% - ${CONTROL_HINT_GAP}px))`,
						// Never swallow the click that is on its way to the control.
						pointerEvents: 'none',
						zIndex: 10,
						background: '#fff',
						color: '#1a1a1a',
						borderRadius: 8,
						padding: '10px 16px',
						fontSize: 14,
						lineHeight: 1.2,
						whiteSpace: 'nowrap',
						boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
					}}
				>
					{controlHint.label}
					{/* Nub pointing at the control, on whichever side the label ended up. */}
					<span
						style={{
							position: 'absolute',
							left: '50%',
							[controlHint.below ? 'top' : 'bottom']: -4,
							width: 10,
							height: 10,
							background: '#fff',
							transform: 'translateX(-50%) rotate(45deg)'
						}}
					/>
				</div>
			)}
			{connectionIssue === 'failed' ? (
				<div
					role="alertdialog"
					style={{
						position: 'absolute',
						inset: 0,
						zIndex: 1000,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						background: 'rgba(255, 255, 255, 0.85)'
					}}
				>
					<div style={{
						maxWidth: 380,
						padding: 24,
						borderRadius: 8,
						background: '#fff',
						boxShadow: '0 2px 12px rgba(0, 0, 0, 0.15)',
						textAlign: 'center',
						fontSize: 15,
						lineHeight: 1.4
					}}>
						<strong style={{ display: 'block', marginBottom: 8 }}>De ontwerptool kon niet goed laden</strong>
						Je internetverbinding is te traag of onderbroken. Controleer je verbinding en probeer het opnieuw.
						<button
							onClick={() => retryEssentialLoadsRef.current && retryEssentialLoadsRef.current()}
							style={{
								display: 'block',
								margin: '16px auto 0',
								padding: '10px 20px',
								border: 'none',
								borderRadius: 8,
								background: '#D8127D',
								color: '#fff',
								fontSize: 15,
								cursor: 'pointer'
							}}
						>
							Opnieuw proberen
						</button>
					</div>
				</div>
			) : (isOffline || connectionIssue === 'slow') && (
				<div
					role="status"
					style={{
						position: 'absolute',
						top: 8,
						left: '50%',
						transform: 'translateX(-50%)',
						zIndex: 1000,
						padding: '8px 16px',
						borderRadius: 8,
						background: '#333',
						color: '#fff',
						fontSize: 14,
						boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
						pointerEvents: 'none'
					}}
				>
					{isOffline
						? 'Geen internetverbinding. Je wijzigingen worden mogelijk niet opgeslagen.'
						: 'Trage internetverbinding - de ontwerptool wordt nog geladen...'}
				</div>
			)}
		</div>
  	);
}
